import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Pagination,
  Stack,
  Checkbox,
  TextField,
  Tooltip
} from '@mui/material';
import { List, ListItem, ListItemText } from '@mui/material';
import {
  ArrowBack, 
  ExpandMore, 
  CheckCircle, 
  Warning, 
  Info, 
  SyncAlt,
  Search,
  FilterList,
  Sync,
  Refresh,
  Settings,
  ChevronRight,
  Folder,
  FolderOpen,
  Edit,

  Delete,
  InsertDriveFile,
  Description,
  Image,
  PictureAsPdf,
  VideoFile,
  AudioFile,
  Code
} from '@mui/icons-material';

import InputAdornment from '@mui/material/InputAdornment';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + 'synctrack';

// UUID generator compatible with browsers and Node
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const GoogleDriveDatapoint = () => {
  const { flowInstanceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [syncRecords, setSyncRecords] = useState([]);
  const [verifyStatus, setVerifyStatus] = useState({});
  const [error, setError] = useState(null);
  
  // UI State Management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceProjectFilter, setSourceProjectFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Get data from navigation state
  const { masterFlowId, flowName, tenantId } = location.state || {};

  // Google Drive UI state
  const [projects, setProjects] = useState([]);
  const [sourceFolders, setSourceFolders] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingSourceFolders, setLoadingSourceFolders] = useState(false);
  const [selectedSourceProject, setSelectedSourceProject] = useState('');
  const [selectedSourceFolders, setSelectedSourceFolders] = useState([]);
  const [folderTree, setFolderTree] = useState({});
  const [loadingFolderChildren, setLoadingFolderChildren] = useState({});
  const [expandedFolders, setExpandedFolders] = useState([]);

  const [googleDriveMappings, setGoogleDriveMappings] = useState([]); // [{srcProjectId, srcProjectName, srcFolderIds[], dstGoogleDriveFolderId}]
  const [googleDriveMappingParams, setGoogleDriveMappingParams] = useState([]);
  const [mappingsCollapsed, setMappingsCollapsed] = useState(true);
  const [editingIndex, setEditingIndex] = useState(null); // number | null
  const [isRunning, setIsRunning] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [availableHubs, setAvailableHubs] = useState([]);
  const [loadingHubs, setLoadingHubs] = useState(false);
  const [autodeskDocsConnector, setAutodeskDocsConnector] = useState(null);
  const [autodeskConnected, setAutodeskConnected] = useState(false);

  
  // Google Drive target folder selection
  const [googleDriveConnector, setGoogleDriveConnector] = useState(null);
  const [googleDriveConnectorConnected, setGoogleDriveConnectorConnected] = useState(false); // Track connection status
  const [googleDriveConnectionId, setGoogleDriveConnectionId] = useState(null); // Assigned connector token ID
  const [googleDriveFolders, setGoogleDriveFolders] = useState([]);
  const [loadingGoogleDriveFolders, setLoadingGoogleDriveFolders] = useState(false);
  const [selectedTargetGoogleDriveFolder, setSelectedTargetGoogleDriveFolder] = useState('');
  
  // Cache for Google Drive folder names (folderId -> folderName)
  // Using object instead of Map for React state reactivity
  const [folderNameCache, setFolderNameCache] = useState({});
  const [fetchingFolderNames, setFetchingFolderNames] = useState({});
  // Cache for failed folder IDs (404 or other permanent errors) to prevent infinite retries
  const [failedFolderIds, setFailedFolderIds] = useState(new Set());

  // Enhanced folder tree traversal state
  // Autodesk source folder/file tree
  const [autodeskFolderTree, setAutodeskFolderTree] = useState({}); // { folderId: { folders: [], files: [] } }
  const [autodeskExpandedNodes, setAutodeskExpandedNodes] = useState([]); // Array of expanded folder IDs
  const [autodeskSelectedFolders, setAutodeskSelectedFolders] = useState([]); // Array of { id, name, path, projectId }
  const [autodeskLoadingNodes, setAutodeskLoadingNodes] = useState({}); // { folderId: true/false }
  const [mappingName, setMappingName] = useState('');
  
  // Google Drive target folder/file tree
  const [googleDriveFolderTree, setGoogleDriveFolderTree] = useState({}); // { folderId: { folders: [], files: [] } }
  const [googleDriveExpandedNodes, setGoogleDriveExpandedNodes] = useState([]); // Array of expanded folder IDs
  const [googleDriveSelectedFolder, setGoogleDriveSelectedFolder] = useState(null); // { id, name, path } or null
  const [googleDriveLoadingNodes, setGoogleDriveLoadingNodes] = useState({}); // { folderId: true/false }
  const [googleDriveAccessToken, setGoogleDriveAccessToken] = useState(null); // Cached access token
  
  // Filtering state
  const [filterSourceProject, setFilterSourceProject] = useState('all');
  const [filterTargetFolder, setFilterTargetFolder] = useState('all');
  
  // Mapping Viewer state (read-only)
  const [viewMappingIndex, setViewMappingIndex] = useState(0);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewSourceFolders, setViewSourceFolders] = useState([]);
  const [viewTargetFolders, setViewTargetFolders] = useState([]);
  const [viewSourceExpandedFolders, setViewSourceExpandedFolders] = useState({});
  const [viewTargetExpandedFolders, setViewTargetExpandedFolders] = useState({});
  const [viewSubfolderContents, setViewSubfolderContents] = useState({});

  // Function to load mappings from saved parameters
  const loadSavedMappings = async (masterFlowId) => {

    if (!masterFlowId) {
      console.warn('⚠️ loadSavedMappings: No masterFlowId provided');
      setGoogleDriveMappingParams([]);
      return;
    }
    try {

      console.log('🔄 Loading saved Google Drive mappings for flow:', masterFlowId);
      const flowParamsUrl = `${API_BASE_URL}/sync-flow-properties/${masterFlowId}`;

      console.log('📡 Fetching from:', flowParamsUrl);
      const fpResp = await fetch(flowParamsUrl, {
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (fpResp.ok) {
        const params = await fpResp.json();

        const mappingParams = Array.isArray(params)
          ? params.filter(p => p?.sfpName && (p.sfpName === 'GoogleDriveMappings' || p.sfpName.startsWith('GoogleDriveMappings_')))
          : [];
        setGoogleDriveMappingParams(mappingParams);

        console.log('📦 Loaded parameters count:', params?.length || 0);
        if (Array.isArray(params)) {
          const asMap = new Map();
          params.forEach(p => {
            const name = p.sfpName || p.mfpName || p.name;
            const val = p.sfpValue || p.mfpValue || p.value;
            if (name) asMap.set(name, val);
          });
          // Always check for chunked parts first (they take priority over placeholder)
          const parts = [...asMap.keys()]
            .filter(k => k && k.startsWith('GoogleDriveMappings_'))
            .sort((a,b) => {
              try {
                const ia = parseInt(a.substring('GoogleDriveMappings_'.length), 10);
                const ib = parseInt(b.substring('GoogleDriveMappings_'.length), 10);
                return ia - ib;
              } catch { return a.localeCompare(b); }
            })
            .map(k => asMap.get(k));
          

          console.log('📦 Found chunked parts:', parts.length);
          
          let mappingsJson = null;
          if (parts.length > 0) {
            // Use chunked parts if they exist
            mappingsJson = parts.join('');

            console.log('📦 Using chunked mappings, total length:', mappingsJson.length);
          } else {
            // Fall back to single GoogleDriveMappings parameter if no chunks
            mappingsJson = asMap.get('GoogleDriveMappings');

            console.log('📦 Using single GoogleDriveMappings parameter, length:', mappingsJson?.length || 0);
          }
          
          // Only set mappings if we have valid JSON (not empty or placeholder)
          if (mappingsJson && mappingsJson.trim().length > 0 && mappingsJson.trim() !== '[]') {
            try {
              const parsed = JSON.parse(mappingsJson);

              console.log('📦 Parsed mappings count:', parsed?.length || 0);
              if (Array.isArray(parsed) && parsed.length > 0) {

                console.log('✅ Setting Google Drive mappings:', parsed);
                setGoogleDriveMappings(parsed);
                return parsed;

              } else {
                console.warn('⚠️ Parsed mappings is empty array');
              }
            } catch (e) {

              console.error('❌ Failed to parse GoogleDriveMappings:', e);
              console.error('❌ Raw JSON string (first 200 chars):', mappingsJson.substring(0, 200));
            }

          } else {
            console.warn('⚠️ No valid mappings JSON found (empty or placeholder)');
          }
        } else {
          setGoogleDriveMappingParams([]);
        }

      } else {
        console.error('❌ Failed to fetch flow parameters:', fpResp.status, await fpResp.text().catch(() => ''));
      }
    } catch (e) {

      console.error('❌ Error loading saved mappings:', e);
      setGoogleDriveMappingParams([]);
    }
    return null;
  };


  // Load mappings when component mounts or config changes
  // This is a backup in case fetchDataPointConfig doesn't load them
  useEffect(() => {
    const loadMappings = async () => {
      if (config?.masterFlowId) {

        // Always reload mappings when config is available to ensure we have the latest
          await loadSavedMappings(config.masterFlowId);
      }
    };
    loadMappings();
  }, [config?.masterFlowId]);


  // Fetch folder names for unknown folders when mappings or sync records change
  useEffect(() => {
    if (!googleDriveConnector?.mcId || googleDriveFolders.length === 0) return;

    const folderIdsToFetch = new Set();

    // Collect folder IDs from mappings
    googleDriveMappings.forEach(m => {
      const folderId = m.dstGoogleDriveFolderId || m.dstFolderId;
      if (folderId && folderId.trim() !== '' && folderId !== 'root') {
        // Only fetch if not in cache and not in root folders
        if (!folderNameCache[folderId] && !googleDriveFolders.find(f => f.id === folderId)) {
          folderIdsToFetch.add(folderId);
        }
      }
    });

    // Collect folder IDs from sync records
    syncRecords.forEach(record => {
      const folderId = record.targetFolderId || record.dstFolderId;
      if (folderId && folderId.trim() !== '' && folderId !== 'root') {
        // Only fetch if not in cache and not in root folders
        if (!folderNameCache[folderId] && !googleDriveFolders.find(f => f.id === folderId)) {
          folderIdsToFetch.add(folderId);
        }
      }
    });

    if (folderIdsToFetch.size > 0) {
      console.log('📦 Fetching folder names for', folderIdsToFetch.size, 'unknown folders');
      fetchMultipleFolderNames(Array.from(folderIdsToFetch));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleDriveMappings, syncRecords, googleDriveFolders, googleDriveConnector?.mcId]);

  useEffect(() => {
    if (flowInstanceId && tenantId) {
      fetchDataPointConfig();
    } else {
      setError('Missing required parameters');
      setLoading(false);
    }
  }, [flowInstanceId, tenantId]);

  const fetchDataPointConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      
      // Step 1: Get data point configuration (connector IDs)
      const configResponse = await fetch(`${API_BASE_URL}/datapoint/flow/${flowInstanceId}/tenant/${tenantId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!configResponse.ok) {
        throw new Error(`Failed to fetch config: ${configResponse.status}`);
      }

      const configData = await configResponse.json();
      configData.flowIdForConnector = configData.flowInstanceId || configData.masterFlowId;
      console.log('Data Point config:', configData);
      setConfig(configData);
      
      // Step 2: Fetch sync records
      let records = [];
      if (configData.masterFlowId) {
        records = await fetchSyncRecords(configData.masterFlowId, token);
      }
      
      setSyncRecords(records);
      
      // Step 2.5: Fetch connector assignments to determine which connectors are actually assigned
      let connectorAssignments = [];
      if (configData.masterFlowId) {
        try {
          const assignmentResponse = await fetch(`${API_BASE_URL}/flow-connector-assignment/flow/${configData.masterFlowId}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          if (assignmentResponse.ok) {
            connectorAssignments = await assignmentResponse.json();
            console.log('GoogleDriveDatapoint: Connector assignments:', connectorAssignments);
          } else {
            console.warn('GoogleDriveDatapoint: Failed to fetch connector assignments:', assignmentResponse.status, assignmentResponse.statusText);
          }
        } catch (error) {
          console.warn('GoogleDriveDatapoint: Failed to fetch connector assignments:', error);
        }
      }
      
      // Step 3: Initialize Autodesk Docs connector if present
      console.log('GoogleDriveDatapoint: All connectors:', configData.connectors);
      // For Google Drive flows, the source connector is named 'Autodesk' (not 'Autodesk Docs')
      // This is the connector that provides Autodesk Docs API access
      const foundConnector = configData.connectors?.find(c => 
        c.mcName === 'Autodesk' || c.mcName === 'Autodesk Docs'
      );
      console.log('GoogleDriveDatapoint: Autodesk connector found:', foundConnector);
      
      if (foundConnector && foundConnector.mcId) {
        // Check if this connector is assigned to the flow
        const autodeskAssignment = connectorAssignments.find(a => 
          (a.connectorType === 'Autodesk Docs' || a.connectorType === 'Autodesk') && 
          a.connectorId
        );
        
        // Enrich connector with assignment info
        const enrichedConnector = {
          ...foundConnector,
          connectionId: autodeskAssignment?.connectorId || foundConnector.connectionId || foundConnector.ctdId || foundConnector.id,
          assigned: Boolean(autodeskAssignment?.connectorId),
          status: autodeskAssignment?.connectorStatus || foundConnector.status,
          connected: autodeskAssignment?.connectorStatus === 'Connected'
        };
        
        setAutodeskDocsConnector(enrichedConnector);
        const autodeskStatus = evaluateConnectorStatus(enrichedConnector);
        setAutodeskConnected(autodeskStatus.connected);

        if (!autodeskStatus.assigned) {
          // toast.warning('Autodesk Docs connector not assigned to this flow. Source configuration will be hidden until assignment.');
        } else if (!autodeskStatus.connected) {
          toast.warning('Assigned Autodesk Docs connector is disconnected. Source configuration will be hidden until it reconnects.');
        }

        if (autodeskStatus.connected) {
          console.log('GoogleDriveDatapoint: Fetching hubs with mcId:', foundConnector.mcId);
          await fetchHubs(foundConnector.mcId, configData.flowIdForConnector, tenantId);
        } else {
          setAvailableHubs([]);
          setProjects([]);
        }
      } else {
        console.warn('GoogleDriveDatapoint: No Autodesk Docs connector found or mcId is missing');
        setAutodeskDocsConnector(null);
        setAutodeskConnected(false);
        toast.warning('Autodesk Docs connector not found. Please ensure the flow has an Autodesk Docs connector configured.');
      }
      

      // Step 4: Initialize Google Drive connector if present and connected
      const googleDriveConn = configData.connectors?.find(c => 
        c.mcName === 'Google Drive' || c.mcName === 'GoogleDrive'
      );
      console.log('GoogleDriveDatapoint: Google Drive connector found:', googleDriveConn);
      console.log('GoogleDriveDatapoint: All connectors structure:', configData.connectors?.map(c => ({
        mcName: c.mcName,
        mcId: c.mcId,
        id: c.id,
        scId: c.scId,
        connectionId: c.connectionId
      })));
      
      if (googleDriveConn && googleDriveConn.mcId) {
        // Check if this connector is assigned to the flow
        const googleDriveAssignment = connectorAssignments.find(a => 
          (a.connectorType === 'Google Drive' || a.connectorType === 'GoogleDrive') && 
          a.connectorId
        );
        
        // Enrich connector with assignment info
        const enrichedGoogleConnector = {
          ...googleDriveConn,
          connectionId: googleDriveAssignment?.connectorId || googleDriveConn.connectionId || googleDriveConn.ctdId || googleDriveConn.id,
          assigned: Boolean(googleDriveAssignment?.connectorId),
          status: googleDriveAssignment?.connectorStatus || googleDriveConn.status,
          connected: googleDriveAssignment?.connectorStatus === 'Connected'
        };
        
        setGoogleDriveConnector(enrichedGoogleConnector);
        
        // Evaluate connector status with assignment info
        const googleStatus = evaluateConnectorStatus(enrichedGoogleConnector);
        setGoogleDriveConnectorConnected(googleStatus.connected);
        setGoogleDriveConnectionId(googleStatus.connected ? enrichedGoogleConnector.connectionId : null);

        if (!googleStatus.assigned) {
          // toast.warning('No Google Drive connector is assigned to this flow.');
        } else if (!googleStatus.connected) {
          // toast.warning('Assigned Google Drive connector is disconnected. Target data will be hidden until it reconnects.');
        }

        if (googleStatus.connected) {
          // Only fetch folders and initialize tree if connector is connected
          await fetchGoogleDriveFolders(enrichedGoogleConnector.mcId, null, enrichedGoogleConnector, configData.masterFlowId, tenantId);
          // Initialize Google Drive tree
          await initializeGoogleDriveTree();
        } else {
          console.warn('GoogleDriveDatapoint: Google Drive connector found but not connected');
          // Ensure all folder data is cleared when connector is disconnected
          setGoogleDriveFolders([]);
          setGoogleDriveFolderTree({});
          setGoogleDriveAccessToken(null);
          setGoogleDriveExpandedNodes([]);
          setGoogleDriveSelectedFolder(null);
          setSelectedTargetGoogleDriveFolder('');
          setFolderNameCache({});
        }
      } else {
        console.warn('GoogleDriveDatapoint: No Google Drive connector found');
        setGoogleDriveConnector(null);
        setGoogleDriveConnectorConnected(false);
        setGoogleDriveConnectionId(null);
      }
      
      // Step 5: Load saved mappings after config is loaded
      if (configData.masterFlowId) {
        await loadSavedMappings(configData.masterFlowId);
      }
      
    } catch (error) {
      console.error('Error fetching data point config:', error);
      setError(error.message);
      toast.error('Failed to load data point configuration');
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncRecords = async (masterFlowId, token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/datapoint/sync-records/${masterFlowId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch sync records: ${response.status}`);
      }
      
      const data = await response.json();
      return data.records || [];
    } catch (error) {
      console.error('Error fetching sync records:', error);
      return [];
    }
  };

  const formatVerificationStatus = (status) => {
    if (!status) return '';
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'mismatch':
        return 'Mismatch';
      case 'source_missing':
        return 'Source Missing';
      case 'target_missing':
        return 'Target Missing';
      case 'error':
        return 'Error';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getVerificationChipStyles = (status) => {
    switch (status) {
      case 'verified':
        return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
      case 'mismatch':
        return { backgroundColor: '#fff3e0', color: '#f57c00' };
      case 'source_missing':
      case 'target_missing':
        return { backgroundColor: '#ffebee', color: '#c62828' };
      default:
        return { backgroundColor: '#eceff1', color: '#455a64' };
    }
  };

  const renderVerificationTooltipContent = (result) => {
    if (!result) {
      return <Typography variant="body2">Verify whether the file exists in Autodesk Docs and Google Drive.</Typography>;
    }

    const checks = Array.isArray(result.checks) ? result.checks : [];

    return (
      <Box sx={{ maxWidth: 320 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {result.message || 'Verification completed'}
        </Typography>
        {checks.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {checks.map((check) => (
              <Typography
                key={check.name}
                variant="caption"
                sx={{
                  display: 'block',
                  color: check.status === 'failed' ? 'error.main' : 'success.main',
                  mb: 0.3
                }}
              >
                {check.status === 'failed' ? '✖' : '✔'} {check.detail}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  const verifySyncRecord = async (record) => {
    if (!record?.logId) {
      toast.warn('Verification is unavailable for this record (missing log reference).');
      return;
    }

    setVerifyStatus((prev) => ({
      ...prev,
      [record.logId]: {
        ...prev[record.logId],
        loading: true
      }
    }));

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/datapoint/google-drive/verify`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          logId: record.logId,
          flowId: config?.masterFlowId || masterFlowId,
          tenantId
        }),
      });

      const data = await response.json();

      setVerifyStatus((prev) => ({
        ...prev,
        [record.logId]: {
          loading: false,
          data
        }
      }));

      if (response.ok) {
        toast.success(data.message || 'Verification completed successfully');
      } else {
        toast.error(data.message || 'Verification failed');
      }
    } catch (err) {
      console.error('Error verifying sync record:', err);
      setVerifyStatus((prev) => ({
        ...prev,
        [record.logId]: {
          loading: false,
          error: err.message,
          data: prev[record.logId]?.data
        }
      }));
      toast.error(`Verification failed: ${err.message}`);
    }
  };

  // Google Drive configuration helpers
  const AUTODESK_DOCS_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/autodesk/docs`;


  const fetchHubs = async (mcId, flowIdParam, tenantIdParam) => {
    if (!mcId) {
      console.warn('fetchHubs: No mcId provided');
      return;
    }

    const flowIdForRequest = flowIdParam || config?.flowIdForConnector || config?.masterFlowId;
    const tenantIdValue = tenantIdParam || tenantId;

    if (!flowIdForRequest) {
      console.warn('fetchHubs: No flowId available');
      return;
    }
    if (!tenantIdValue) {
      console.warn('fetchHubs: No tenantId available');
      return;
    }
    setLoadingHubs(true);
    try {
      const url = `${AUTODESK_DOCS_API_URL}/hubs/by-connector?mcId=${mcId}&flowId=${flowIdForRequest}&tenantId=${tenantIdValue}`;
      console.log('fetchHubs: Fetching from URL:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const rawText = await response.text();
      let data = {};
      try {
        data = rawText ? JSON.parse(rawText) : {};
      } catch {
        data = {};
      }
      const apiMessage = typeof data.message === 'string' ? data.message : null;
      const hasErrorFlag = data.error === true || data.error === 'Connector not assigned to flow';
      if (!response.ok || hasErrorFlag) {
        const msg = apiMessage || (typeof data.error === 'string' ? data.error : null) || `HTTP ${response.status}`;
        console.error('fetchHubs: HTTP error!', response.status, rawText);
        setAvailableHubs([]);
        toast.error(msg || 'Could not load Autodesk Docs hubs.');
        return;
      }

      console.log('fetchHubs: Response data:', data);

      let hubs = [];
      if (data.results && Array.isArray(data.results)) {
        hubs = data.results;
      } else if (data.data && Array.isArray(data.data)) {
        hubs = data.data;
      } else if (Array.isArray(data)) {
        hubs = data;
      }

      console.log('fetchHubs: Extracted hubs:', hubs);
      setAvailableHubs(hubs);
    } catch (error) {
      console.error('fetchHubs: Error fetching hubs:', error);
      setAvailableHubs([]);
      toast.error(error?.message || 'Failed to load accounts');
    } finally {
      setLoadingHubs(false);
    }
  };


  const fetchProjects = async (mcId, accountIdParam, masterFlowIdParam, tenantIdParam) => {
    if (!mcId || !accountIdParam) {
      setProjects([]);
      return;
    }

    const masterFlowId = masterFlowIdParam || config?.masterFlowId;
    const tenantIdValue = tenantIdParam || tenantId;
    
    if (!masterFlowId) {
      console.warn('fetchProjects: No masterFlowId available');
      setProjects([]);
      return;
    }
    if (!tenantIdValue) {
      console.warn('fetchProjects: No tenantId available');
      setProjects([]);
      return;
    }
    setLoadingProjects(true);
    try {

      const url = `${AUTODESK_DOCS_API_URL}/projects/by-connector?mcId=${mcId}&flowId=${config?.flowIdForConnector ?? masterFlowId}&tenantId=${tenantIdValue}&accountId=${encodeURIComponent(accountIdParam)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setProjects(data.results || []);
    } catch (error) {
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };


  const fetchFolders = async (mcId, projectId, masterFlowIdParam, tenantIdParam) => {
    if (!mcId || !projectId) return;

    const masterFlowId = masterFlowIdParam || config?.masterFlowId;
    const tenantIdValue = tenantIdParam || tenantId;
    
    if (!masterFlowId) {
      console.warn('fetchFolders: No masterFlowId available');
      return;
    }
    if (!tenantIdValue) {
      console.warn('fetchFolders: No tenantId available');
      return;
    }
    setLoadingSourceFolders(true);
    try {

      const response = await fetch(`${AUTODESK_DOCS_API_URL}/folders/by-project?mcId=${mcId}&flowId=${config?.flowIdForConnector ?? masterFlowId}&tenantId=${tenantIdValue}&projectId=${projectId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      const list = data.results || data.data || [];
      setSourceFolders(list);
    } catch (e) {
      setSourceFolders([]);
    } finally {
      setLoadingSourceFolders(false);
    }
  };


  const fetchFolderChildren = async (mcId, projectId, folderId, masterFlowIdParam, tenantIdParam) => {
    if (!mcId || !projectId || !folderId) return [];

    const masterFlowId = masterFlowIdParam || config?.masterFlowId;
    const tenantIdValue = tenantIdParam || tenantId;
    
    if (!masterFlowId) {
      console.warn('fetchFolderChildren: No masterFlowId available');
      return [];
    }
    if (!tenantIdValue) {
      console.warn('fetchFolderChildren: No tenantId available');
      return [];
    }
    const loadingKey = `${folderId}`;
    setLoadingFolderChildren(prev => ({ ...prev, [loadingKey]: true }));
    try {

      const response = await fetch(`${AUTODESK_DOCS_API_URL}/folders/children?mcId=${mcId}&flowId=${config?.flowIdForConnector ?? masterFlowId}&tenantId=${tenantIdValue}&projectId=${projectId}&folderId=${folderId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) return [];
      const data = await response.json();
      const children = data.results || data.data || [];
      setFolderTree(prev => ({ ...prev, [folderId]: children }));
      return children;
    } catch (e) {
      return [];
    } finally {
      setLoadingFolderChildren(prev => ({ ...prev, [loadingKey]: false }));
    }
  };


  // Fetch Google Drive folders
  const GOOGLE_DRIVE_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/google`;
  
  const fetchGoogleDriveFolders = async (mcId, parentFolderId = null, connectorObj = null, masterFlowIdOverride = null, tenantIdOverride = null) => {
    const effectiveMasterFlowId = masterFlowIdOverride || config?.masterFlowId;
    const effectiveTenantId = tenantIdOverride || tenantId;
    
    if (!mcId || !effectiveMasterFlowId) {
      console.warn('⚠️ Missing mcId or masterFlowId:', { mcId, masterFlowId: effectiveMasterFlowId, fromOverride: !!masterFlowIdOverride, fromConfig: !!config?.masterFlowId });
      return;
    }
    setLoadingGoogleDriveFolders(true);
    try {
      console.log('🔍 === FETCHING GOOGLE DRIVE FOLDERS ===');
      console.log('📋 Input params:', { mcId, parentFolderId, hasConnectorObj: !!connectorObj });
      
      // Get the sync connector ID (connection ID) from the master connector ID
      // Try with tenant ID first if available, otherwise without
      let connectionId = null;
      const syncConnectorUrl = effectiveTenantId 
        ? `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors/mc/${mcId}/tenant/${effectiveTenantId}`
        : `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors/mc/${mcId}`;
      
      console.log('📡 Fetching sync connectors from:', syncConnectorUrl);
      const connectorsResponse = await fetch(syncConnectorUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      console.log('📦 Sync connectors response status:', connectorsResponse.status);
      
      if (connectorsResponse.ok) {
        const syncConnectors = await connectorsResponse.json();
        console.log('📦 Sync connectors response data:', syncConnectors);
        console.log('📦 Is array?', Array.isArray(syncConnectors));
        console.log('📦 Length:', Array.isArray(syncConnectors) ? syncConnectors.length : 'N/A');
        
        if (Array.isArray(syncConnectors) && syncConnectors.length > 0) {
          console.log('📦 All connectors:', syncConnectors.map(c => ({
            id: c.id,
            scId: c.scId,
            scRecId: c.scRecId,
            status: c.status,
            mcId: c.mcId
          })));
          
          // Get the first connected connector (or the first one if none are connected)
          const connectedConnector = syncConnectors.find(c => c.status === 'Connected') || syncConnectors[0];
          console.log('📦 Selected connector:', connectedConnector);
          
          connectionId = connectedConnector.id || connectedConnector.scId || connectedConnector.scRecId;
          console.log('✅ Found sync connector ID:', connectionId);
        } else {
          console.warn('⚠️ No sync connectors found for mcId:', mcId);
          if (!Array.isArray(syncConnectors)) {
            console.warn('⚠️ Response is not an array:', typeof syncConnectors, syncConnectors);
          }
        }
      } else {
        const errorText = await connectorsResponse.text();
        console.error('❌ Failed to fetch sync connectors:', connectorsResponse.status, errorText);
        // Try without tenant ID if first attempt failed
        if (effectiveTenantId) {
          console.log('🔄 Retrying without tenant ID...');
          const retryResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors/mc/${mcId}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          });
          if (retryResponse.ok) {
            const retryConnectors = await retryResponse.json();
            if (Array.isArray(retryConnectors) && retryConnectors.length > 0) {
              const connector = retryConnectors.find(c => c.status === 'Connected') || retryConnectors[0];
              connectionId = connector.id || connector.scId || connector.scRecId;
              console.log('✅ Found sync connector ID (retry):', connectionId);
            }
          }
        }
      }
      
      if (!connectionId) {
        console.warn('⚠️ Could not find connection ID, cannot fetch folders');
        const errorMsg = 'Google Drive connector not connected. Please connect the connector first.';
        toast.warning(errorMsg);
        setGoogleDriveFolders([]);
        return;
      }
      
      // Use the assigned connector if available, otherwise use the connectionId from sync connectors
      const connectorIdToUse = googleDriveConnectionId || connectionId;
      
      if (!connectorIdToUse) {
        console.warn('⚠️ Could not find connector ID for token fetch');
        const errorMsg = 'Google Drive connector not connected. Please connect the connector first.';
        toast.warning(errorMsg);
        setGoogleDriveFolders([]);
        return;
      }
      
      // Get valid token for the specific connector (assigned or selected)
      console.log('🔑 Getting Google Drive token for connector:', connectorIdToUse);
      const tokenResponse = await fetch(`${GOOGLE_DRIVE_API_URL}/validToken?ctdId=${connectorIdToUse}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      console.log('🔑 Token response status:', tokenResponse.status);
      
      if (!tokenResponse.ok) {
        const errorText = await tokenResponse.text();
        console.error('❌ Token fetch failed:', tokenResponse.status, errorText);
        throw new Error(`Failed to get Google Drive token (${tokenResponse.status}): ${errorText}`);
      }
      
      const tokenData = await tokenResponse.json();
      console.log('🔑 Token response data (full):', JSON.stringify(tokenData, null, 2));
      console.log('🔑 Token response data keys:', Object.keys(tokenData));
      
      // Handle different response formats
      let accessToken = tokenData.access_token || tokenData.token || tokenData.accessToken;
      
      if (!accessToken) {
        console.error('❌ No access token in response:', tokenData);
        console.error('❌ Available keys:', Object.keys(tokenData));
        throw new Error('No access token received from Google Drive service. Response: ' + JSON.stringify(tokenData));
      }
      
      // Log token details (without exposing full token for security)
      console.log('✅ Got access token');
      console.log('   - Length:', accessToken.length);
      console.log('   - First 30 chars:', accessToken.substring(0, 30) + '...');
      console.log('   - Last 10 chars:', '...' + accessToken.substring(accessToken.length - 10));
      
      // Validate token format (Google OAuth tokens typically start with specific patterns)
      if (!accessToken.startsWith('ya29.') && !accessToken.startsWith('1//')) {
        console.warn('⚠️ Token format may be unexpected - Google tokens usually start with ya29. or 1//');
        console.warn('⚠️ Actual token starts with:', accessToken.substring(0, 10));
      } else {
        console.log('✅ Token format looks valid (starts with expected pattern)');
      }
      
      // Check if token is empty or just whitespace
      if (!accessToken.trim()) {
        console.error('❌ Token is empty or whitespace only!');
        throw new Error('Access token is empty');
      }
      
      // Use Google Drive API to list folders
      // For root folders, query folders that are directly in root (have 'root' in parents)
      const parentId = parentFolderId || 'root';
      let query;
      if (parentId === 'root') {
        // Query for folders directly in root
        query = `mimeType = 'application/vnd.google-apps.folder' and 'root' in parents and trashed = false`;
      } else {
        // Query for folders in a specific parent
        query = `mimeType = 'application/vnd.google-apps.folder' and '${parentId}' in parents and trashed = false`;
      }
      
      const url = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&fields=files(id,name,parents)&pageSize=1000&supportsAllDrives=true&includeItemsFromAllDrives=true`;
      
      console.log('📡 Fetching folders from Google Drive API');
      console.log('📡 Query:', query);
      console.log('📡 Full URL (truncated):', url.substring(0, 200) + '...');
      console.log('📡 Using Authorization header with token (first 30 chars):', accessToken.substring(0, 30) + '...');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      
      // Log the actual Authorization header being sent (first part only for security)
      console.log('📡 Authorization header sent:', `Bearer ${accessToken.substring(0, 30)}...`);
      
      console.log('📡 Google Drive API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Google Drive API error:', response.status, errorText);
        throw new Error(`Google Drive API error (${response.status}): ${errorText}`);
      }
      
      const data = await response.json();
      console.log('📦 Google Drive API response:', data);
      console.log('📦 Response keys:', Object.keys(data));
      const folders = data.files || [];
      console.log(`✅ Found ${folders.length} folders`);
      
      if (folders.length > 0) {
        console.log('📦 First folder example:', folders[0]);
      }
      
      // Format folders for display
      const formattedFolders = folders.map(f => ({
        id: f.id,
        name: f.name,
        parents: f.parents || []
      }));
      
      console.log('📦 Formatted folders count:', formattedFolders.length);
      setGoogleDriveFolders(formattedFolders);
      
      if (formattedFolders.length === 0) {
        console.warn('⚠️ No folders found in Google Drive root');
        toast.info('No folders found in Google Drive root. You can still use root folder.');
      } else {
        console.log('✅ Successfully loaded', formattedFolders.length, 'folders');
      }
    } catch (error) {
      console.error('❌ Error fetching Google Drive folders:', error);
      console.error('❌ Error stack:', error.stack);
      setGoogleDriveFolders([]);
      toast.error(`Failed to load Google Drive folders: ${error.message}`);
    } finally {
      setLoadingGoogleDriveFolders(false);
      console.log('🔍 === FETCH COMPLETE ===');
    }
  };

  // Fetch a single Google Drive folder name by ID
  const fetchGoogleDriveFolderName = async (folderId) => {
    if (!folderId || folderId.trim() === '' || folderId === 'root') {
      return null;
    }

    // Check cache first
    if (folderNameCache[folderId]) {
      return folderNameCache[folderId];
    }

    // Check if already fetching this folder
    if (fetchingFolderNames[folderId]) {
      return null; // Return null, will be updated when fetch completes
    }

    // Check if we have the folder in googleDriveFolders
    const cachedFolder = googleDriveFolders.find(f => f.id === folderId);
    if (cachedFolder) {
      setFolderNameCache(prev => ({ ...prev, [folderId]: cachedFolder.name }));
      return cachedFolder.name;
    }

    // Check if this folder ID has already failed (404, etc.) to prevent infinite retries
    if (failedFolderIds.has(folderId)) {
      console.warn('⚠️ Skipping fetch for failed folder ID:', folderId);
      return null;
    }

    if (!googleDriveConnector?.mcId) {
      console.warn('⚠️ Cannot fetch folder name - Google Drive connector not available');
      return null;
    }

    try {
      setFetchingFolderNames(prev => ({ ...prev, [folderId]: true }));
      console.log('📡 Fetching Google Drive folder name for ID:', folderId);

      // Get connection ID
      const effectiveTenantId = tenantId;
      let connectionId = null;
      const syncConnectorUrl = effectiveTenantId 
        ? `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors/mc/${googleDriveConnector.mcId}/tenant/${effectiveTenantId}`
        : `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors/mc/${googleDriveConnector.mcId}`;
      
      const connectorsResponse = await fetch(syncConnectorUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (connectorsResponse.ok) {
        const syncConnectors = await connectorsResponse.json();
        if (Array.isArray(syncConnectors) && syncConnectors.length > 0) {
          const connectedConnector = syncConnectors.find(c => c.status === 'Connected') || syncConnectors[0];
          connectionId = connectedConnector.id || connectedConnector.scId || connectedConnector.scRecId;
        }
      }

      if (!connectionId) {
        console.warn('⚠️ Could not find connection ID for folder name fetch');
        return null;
      }

      // Get valid token
      const tokenResponse = await fetch(`${GOOGLE_DRIVE_API_URL}/validToken?ctdId=${connectionId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!tokenResponse.ok) {
        console.error('❌ Token fetch failed for folder name:', tokenResponse.status);
        return null;
      }

      const tokenData = await tokenResponse.json();
      const accessToken = tokenData.access_token || tokenData.token || tokenData.accessToken;

      if (!accessToken) {
        console.error('❌ No access token for folder name fetch');
        return null;
      }

      // Fetch folder metadata from Google Drive API
      const url = `https://www.googleapis.com/drive/v3/files/${folderId}?fields=id,name&supportsAllDrives=true`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        console.error('❌ Failed to fetch folder name:', response.status, 'for folderId:', folderId);
        // Cache 404 and other permanent errors to prevent infinite retries
        if (response.status === 404 || response.status === 403 || response.status === 410) {
          setFailedFolderIds(prev => {
            const next = new Set(prev);
            next.add(folderId);
            return next;
          });
          console.warn('⚠️ Caching failed folder ID to prevent retries:', folderId);
        }
        return null;
      }

      const data = await response.json();
      const folderName = data.name;

      if (folderName) {
        // Update cache
        setFolderNameCache(prev => ({ ...prev, [folderId]: folderName }));
        console.log('✅ Fetched folder name:', folderName, 'for ID:', folderId);
        return folderName;
      }

      return null;
    } catch (error) {
      console.error('❌ Error fetching Google Drive folder name:', error);
      // Cache network errors and other failures to prevent infinite retries
      // Only cache if it's a fetch error (network issue) or if error indicates file not found
      if (error.message?.includes('Failed to fetch') || error.message?.includes('404') || error.message?.includes('not found')) {
        setFailedFolderIds(prev => {
          const next = new Set(prev);
          next.add(folderId);
          return next;
        });
        console.warn('⚠️ Caching failed folder ID due to error to prevent retries:', folderId);
      }
      return null;
    } finally {
      setFetchingFolderNames(prev => {
        const next = { ...prev };
        delete next[folderId];
        return next;
      });
    }
  };

  // Batch fetch folder names for multiple folder IDs
  const fetchMultipleFolderNames = async (folderIds) => {
    if (!folderIds || folderIds.length === 0) return;
    
    // Filter out folders we already have in cache or are fetching or have failed
    const foldersToFetch = folderIds.filter(id => {
      if (!id || id.trim() === '' || id === 'root') return false;
      if (folderNameCache[id]) return false;
      if (fetchingFolderNames[id]) return false;
      // Check if in googleDriveFolders
      if (googleDriveFolders.find(f => f.id === id)) return false;
      // Check if this folder ID has already failed (404, etc.) to prevent infinite retries
      if (failedFolderIds.has(id)) return false;
      return true;
    });

    if (foldersToFetch.length === 0) return;

    console.log('📦 Batch fetching folder names for:', foldersToFetch.length, 'folders');
    
    // Fetch all folders in parallel (with limit to avoid overwhelming the API)
    const batchSize = 5;
    for (let i = 0; i < foldersToFetch.length; i += batchSize) {
      const batch = foldersToFetch.slice(i, i + batchSize);
      await Promise.all(batch.map(id => fetchGoogleDriveFolderName(id)));
    }
  };

  const handleFolderSelect = (folderId, folderName, checked, folderPath = null) => {
    if (checked) {

      // Use provided path, or fallback to just the folder name
      const path = folderPath || folderName;
      setSelectedSourceFolders(prev => [...prev, { id: folderId, name: folderName, path: path }]);
    } else {
      setSelectedSourceFolders(prev => prev.filter(f => f.id !== folderId));
    }
  };


  // ==================== Enhanced Folder Tree Functions ====================
  
  // Fetch Autodesk folder contents (folders + files) for tree view
  const fetchAutodeskFolderContents = async (mcId, projectId, folderId, masterFlowIdParam, tenantIdParam) => {
    if (!mcId || !projectId) return;
    const masterFlowId = masterFlowIdParam || config?.masterFlowId;
    const tenantIdValue = tenantIdParam || tenantId;
    
    if (!masterFlowId || !tenantIdValue) {
      console.warn('fetchAutodeskFolderContents: Missing masterFlowId or tenantId');
      return;
    }

    setAutodeskLoadingNodes(prev => ({ ...prev, [folderId]: true }));
    try {
      // Fetch folder contents (includes both folders and files)
      const url = `${AUTODESK_DOCS_API_URL}/source-folder-contents/by-connector?mcId=${mcId}&flowId=${config?.flowIdForConnector ?? masterFlowId}&tenantId=${tenantIdValue}&projectId=${encodeURIComponent(projectId)}&folderId=${encodeURIComponent(folderId)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const items = data.results || data.data || [];
      
      // Separate folders and files
      const folders = items.filter(item => item.type === 'folders' || item.attributes?.extension?.type === 'folders:autodesk.bim360:Folder');
      const files = items.filter(item => item.type === 'items' || item.attributes?.extension?.type === 'items:autodesk.bim360:File');

      setAutodeskFolderTree(prev => ({
        ...prev,
        [folderId]: { folders, files }
      }));
    } catch (error) {
      console.error('Error fetching Autodesk folder contents:', error);
      setAutodeskFolderTree(prev => ({
        ...prev,
        [folderId]: { folders: [], files: [] }
      }));
    } finally {
      setAutodeskLoadingNodes(prev => ({ ...prev, [folderId]: false }));
    }
  };

  // Fetch Google Drive folder contents (folders + files) for tree view
  const fetchGoogleDriveFolderContents = async (folderId = 'root', accessToken = null) => {
    // Ensure we're using the correct connector's token
    if (!googleDriveConnectionId) {
      console.warn('No Google Drive connector assigned - cannot fetch folder contents');
      return;
    }
    
    if (!accessToken && !googleDriveAccessToken) {
      // Get token if not provided - this will use the selected connector
      const token = await getGoogleDriveAccessToken();
      if (!token) {
        console.error('No access token available for Google Drive connector:', googleDriveConnectionId);
        return;
      }
      setGoogleDriveAccessToken(token);
      accessToken = token;
    } else if (!accessToken) {
      // Use cached token, but verify it's for the current connector
      // If connector changed, the token should have been cleared, but double-check
      if (googleDriveAccessToken) {
        accessToken = googleDriveAccessToken;
      } else {
        // No cached token, get a new one for the current connector
        const token = await getGoogleDriveAccessToken();
        if (!token) {
          console.error('No access token available for Google Drive connector:', googleDriveConnectionId);
          return;
        }
        setGoogleDriveAccessToken(token);
        accessToken = token;
      }
    }
    
    // Log which connector we're using for this fetch
    console.log('Fetching Google Drive folder contents for assigned connector:', googleDriveConnectionId, 'folder:', folderId);

    setGoogleDriveLoadingNodes(prev => ({ ...prev, [folderId]: true }));
    try {
      // Build query for folders and files in the specified folder
      const parentId = folderId === 'root' ? 'root' : folderId;
      const query = `'${parentId}' in parents and trashed = false`;
      
      // Fetch folders
      const foldersQuery = `mimeType = 'application/vnd.google-apps.folder' and ${query}`;
      const foldersUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(foldersQuery)}&fields=files(id,name,parents,mimeType)&pageSize=1000&supportsAllDrives=true&includeItemsFromAllDrives=true`;
      
      // Fetch files (non-folder items)
      const filesQuery = `mimeType != 'application/vnd.google-apps.folder' and ${query}`;
      const filesUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(filesQuery)}&fields=files(id,name,parents,mimeType,size)&pageSize=1000&supportsAllDrives=true&includeItemsFromAllDrives=true`;

      const [foldersResponse, filesResponse] = await Promise.all([
        fetch(foldersUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        }),
        fetch(filesUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        })
      ]);

      if (!foldersResponse.ok || !filesResponse.ok) {
        throw new Error(`HTTP error! folders: ${foldersResponse.status}, files: ${filesResponse.status}`);
      }

      const foldersData = await foldersResponse.json();
      const filesData = await filesResponse.json();

      const folders = (foldersData.files || []).map(f => ({
        id: f.id,
        name: f.name,
        parents: f.parents || [],
        type: 'folder'
      }));

      const files = (filesData.files || []).map(f => ({
        id: f.id,
        name: f.name,
        parents: f.parents || [],
        mimeType: f.mimeType,
        size: f.size ? parseInt(f.size) : null,
        type: 'file'
      }));

      setGoogleDriveFolderTree(prev => ({
        ...prev,
        [folderId]: { folders, files }
      }));
    } catch (error) {
      console.error('Error fetching Google Drive folder contents:', error);
      setGoogleDriveFolderTree(prev => ({
        ...prev,
        [folderId]: { folders: [], files: [] }
      }));
    } finally {
      setGoogleDriveLoadingNodes(prev => ({ ...prev, [folderId]: false }));
    }
  };

  const evaluateConnectorStatus = (connector) => {
    if (!connector) {
      return { assigned: false, connected: false };
    }
    // Use assigned property if explicitly set, otherwise check connectionId
    const assigned = typeof connector.assigned === 'boolean' ? connector.assigned : Boolean(connector.connectionId);
    const statusString = (connector.status || connector.connectionStatus || connector.connectorStatus || '').toString().toLowerCase();
    let connected = assigned;
    // If connected property is explicitly set, use it
    if (typeof connector.connected === 'boolean') {
      connected = assigned && connector.connected;
    } else if (statusString) {
      connected = assigned && statusString === 'connected';
    }
    return { assigned, connected };
  };

  // Helper to get Google Drive access token
  const getGoogleDriveAccessToken = async () => {
    if (!googleDriveConnector?.mcId) {
      console.warn('No Google Drive connector available');
      return null;
    }

    // Check connection status first
    if (!googleDriveConnectorConnected) {
      console.warn('Google Drive connector is not connected');
      return null;
    }

    // Use the assigned/selected connector ID
    if (!googleDriveConnectionId) {
      console.warn('No Google Drive connector selected');
      return null;
    }

    try {
      const connectionId = googleDriveConnectionId;

      if (!connectionId) {
        console.warn('Could not find connection ID');
        setGoogleDriveConnectorConnected(false);
        return null;
      }

      const tokenResponse = await fetch(`${GOOGLE_DRIVE_API_URL}/validToken?ctdId=${connectionId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!tokenResponse.ok) {
        console.warn('Failed to get valid token - connector may be disconnected');
        setGoogleDriveConnectorConnected(false);
        return null;
      }

      const tokenData = await tokenResponse.json();
      const token = tokenData.access_token || tokenData.token || tokenData.accessToken;
      
      if (!token) {
        setGoogleDriveConnectorConnected(false);
        return null;
      }
      
      return token;
    } catch (error) {
      console.error('Error getting Google Drive access token:', error);
      setGoogleDriveConnectorConnected(false);
      return null;
    }
  };

  // Initialize Autodesk folder tree when project is selected
  const initializeAutodeskTree = async (projectId) => {
    if (!autodeskDocsConnector?.mcId || !projectId) return;
    
    // Fetch top-level folders for the project
    await fetchFolders(autodeskDocsConnector.mcId, projectId, config?.masterFlowId, tenantId);
    
    // Also fetch root folder contents (if there's a root folder concept)
    // For now, we'll fetch top-level folders and their contents when expanded
  };

  // Initialize Google Drive folder tree
  const initializeGoogleDriveTree = async (startFolderId = null) => {
    if (!googleDriveConnector?.mcId) {
      // Clear data if no connector
      setGoogleDriveFolderTree({});
      setGoogleDriveAccessToken(null);
      return;
    }
    
    // Check if connector is connected before initializing
    if (!googleDriveConnectorConnected) {
      console.warn('Cannot initialize Google Drive tree - connector is not connected');
      // Ensure all folder data is cleared when disconnected
      setGoogleDriveFolderTree({});
      setGoogleDriveAccessToken(null);
      setGoogleDriveExpandedNodes([]);
      setGoogleDriveSelectedFolder(null);
      setSelectedTargetGoogleDriveFolder('');
      setGoogleDriveFolders([]);
      setFolderNameCache({});
      return;
    }
    
    // Ensure we have a selected connector (assigned or fallback)
    if (!googleDriveConnectionId) {
      console.warn('No Google Drive connector selected - cannot initialize tree');
      // Clear all folder data
      setGoogleDriveFolderTree({});
      setGoogleDriveAccessToken(null);
      setGoogleDriveExpandedNodes([]);
      setGoogleDriveSelectedFolder(null);
      setSelectedTargetGoogleDriveFolder('');
      setGoogleDriveFolders([]);
      setFolderNameCache({});
      return;
    }

    const token = await getGoogleDriveAccessToken();
    if (!token) {
      console.warn('Failed to get Google Drive access token for connector:', googleDriveConnectionId);
      setGoogleDriveConnectorConnected(false);
      // Clear all folder data when token fetch fails
      setGoogleDriveFolderTree({});
      setGoogleDriveAccessToken(null);
      setGoogleDriveExpandedNodes([]);
      setGoogleDriveSelectedFolder(null);
      setSelectedTargetGoogleDriveFolder('');
      setGoogleDriveFolders([]);
      setFolderNameCache({});
      toast.error('Google Drive connector is disconnected. Please reconnect to view folder structure.');
      return;
    }

    setGoogleDriveAccessToken(token);
    console.log('Initializing Google Drive tree for assigned connector:', googleDriveConnectionId);
    
    // Clear any existing folder data to ensure we only show data from this connector
    setGoogleDriveFolderTree({});
    setGoogleDriveExpandedNodes([]);
    
    // If a folder is selected, start from that folder, otherwise start from root
    const folderId = startFolderId || selectedTargetGoogleDriveFolder || 'root';
    await fetchGoogleDriveFolderContents(folderId, token);
  };

  const handleFolderExpand = async (mcId, projectId, folderId) => {
    if (!mcId || !projectId) return;
    if (expandedFolders.includes(folderId)) {
      setExpandedFolders(prev => prev.filter(id => id !== folderId));
    } else {
      setExpandedFolders(prev => [...prev, folderId]);
      if (!folderTree[folderId]) {

        await fetchFolderChildren(mcId, projectId, folderId, config?.masterFlowId, tenantId);
      }
    }
  };


  const FolderTreeItem = ({ folder, level = 0, mcId, projectId, parentPath = '' }) => {
    const folderId = folder.id;
    const folderName = folder.attributes?.displayName || folder.attributes?.name || folder.id;
    const isExpanded = expandedFolders.includes(folderId);
    const children = folderTree[folderId] || [];
    const isLoading = loadingFolderChildren[folderId];
    const isSelected = selectedSourceFolders.some(f => f.id === folderId);

    
    // Build full path: parentPath/folderName (or just folderName if root)
    const currentPath = parentPath ? `${parentPath}/${folderName}` : folderName;
    
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, pl: level * 2, '&:hover': { backgroundColor: 'action.hover' } }}>
          <IconButton size="small" onClick={() => handleFolderExpand(mcId, projectId, folderId)} disabled={isLoading} sx={{ width: 24, height: 24, mr: 0.5 }}>
            {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>

          <Checkbox checked={isSelected} onChange={(e) => handleFolderSelect(folderId, folderName, e.target.checked, currentPath)} size="small" sx={{ p: 0.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, ml: 0.5 }}>
            {isExpanded ? <FolderOpen fontSize="small" color="primary" sx={{ mr: 1 }} /> : <Folder fontSize="small" sx={{ mr: 1 }} />}
            <Typography variant="body2">{folderName}</Typography>
          </Box>
          {isLoading && (<CircularProgress size={16} sx={{ ml: 1 }} />)}
        </Box>
        {isExpanded && children.length > 0 && (
          <Box>
            {children.map((child) => (

              <FolderTreeItem key={child.id} folder={child} level={level + 1} mcId={mcId} projectId={projectId} parentPath={currentPath} />
            ))}
          </Box>
        )}
        {isExpanded && children.length === 0 && !isLoading && (
          <Box sx={{ pl: level * 2 + 4, py: 0.5 }}>
            <Typography variant="caption" color="textSecondary">No subfolders</Typography>
          </Box>
        )}
      </Box>
    );
  };


  // Helper to get file icon based on file name/type
  const getFileIcon = (fileName, mimeType) => {
    if (!fileName) return <InsertDriveFile fontSize="small" />;
    
    const ext = fileName.split('.').pop()?.toLowerCase();
    const mime = mimeType?.toLowerCase() || '';
    
    if (mime.includes('pdf') || ext === 'pdf') return <PictureAsPdf fontSize="small" color="error" />;
    if (mime.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) return <Image fontSize="small" color="primary" />;
    if (mime.includes('video') || ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'].includes(ext)) return <VideoFile fontSize="small" color="secondary" />;
    if (mime.includes('audio') || ['mp3', 'wav', 'flac', 'aac', 'ogg'].includes(ext)) return <AudioFile fontSize="small" color="info" />;
    if (['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json', 'xml'].includes(ext)) return <Code fontSize="small" color="action" />;
    return <Description fontSize="small" />;
  };

  // Enhanced Autodesk Tree Item (folders + files)
  const AutodeskTreeItem = ({ item, level = 0, projectId, parentPath = '' }) => {
    const itemId = item.id;
    const itemName = item.attributes?.displayName || item.attributes?.name || item.id;
    const isFolder = item.type === 'folders' || item.attributes?.extension?.type === 'folders:autodesk.bim360:Folder';
    const isExpanded = autodeskExpandedNodes.includes(itemId);
    const contents = autodeskFolderTree[itemId] || { folders: [], files: [] };
    const isLoading = autodeskLoadingNodes[itemId];
    const isSelected = autodeskSelectedFolders.some(f => f.id === itemId);
    
    const currentPath = parentPath ? `${parentPath}/${itemName}` : itemName;
    
    const handleExpand = async () => {
      if (isExpanded) {
        setAutodeskExpandedNodes(prev => prev.filter(id => id !== itemId));
      } else {
        setAutodeskExpandedNodes(prev => [...prev, itemId]);
        if (!autodeskFolderTree[itemId] && autodeskDocsConnector?.mcId) {
          await fetchAutodeskFolderContents(autodeskDocsConnector.mcId, projectId, itemId, config?.masterFlowId, tenantId);
        }
      }
    };
    
    const handleSelect = (event, checked) => {
      event.stopPropagation();
      if (checked) {
        setAutodeskSelectedFolders(prev => [...prev, { id: itemId, name: itemName, path: currentPath, projectId }]);
      } else {
        setAutodeskSelectedFolders(prev => prev.filter(f => f.id !== itemId));
      }
    };
    
    if (!isFolder) {
      // File item (no expansion, no checkbox)
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, pl: level * 2 + 4, '&:hover': { backgroundColor: 'action.hover' } }}>
          <Box sx={{ width: 24, mr: 0.5 }} /> {/* Spacer for alignment */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, ml: 0.5 }}>
            {getFileIcon(itemName, item.attributes?.extension?.type)}
            <Typography variant="body2" sx={{ ml: 1 }}>{itemName}</Typography>
          </Box>
        </Box>
      );
    }
    
    // Folder item
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, pl: level * 2, '&:hover': { backgroundColor: 'action.hover' } }}>
          <IconButton size="small" onClick={handleExpand} disabled={isLoading} sx={{ width: 24, height: 24, mr: 0.5 }}>
            {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
          <Checkbox
            checked={isSelected}
            onChange={(e) => handleSelect(e, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ p: 0.5 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, ml: 0.5 }}>
            {isExpanded ? <FolderOpen fontSize="small" color="primary" sx={{ mr: 1 }} /> : <Folder fontSize="small" sx={{ mr: 1 }} />}
            <Typography variant="body2">{itemName}</Typography>
          </Box>
          {isLoading && (<CircularProgress size={16} sx={{ ml: 1 }} />)}
        </Box>
        {isExpanded && (
          <Box>
            {/* Render subfolders */}
            {contents.folders.map((folder) => (
              <AutodeskTreeItem key={folder.id} item={folder} level={level + 1} projectId={projectId} parentPath={currentPath} />
            ))}
            {/* Render files */}
            {contents.files.map((file) => (
              <AutodeskTreeItem key={file.id} item={file} level={level + 1} projectId={projectId} parentPath={currentPath} />
            ))}
            {contents.folders.length === 0 && contents.files.length === 0 && !isLoading && (
              <Box sx={{ pl: level * 2 + 4, py: 0.5 }}>
                <Typography variant="caption" color="textSecondary">Empty folder</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // Enhanced Google Drive Tree Item (folders + files)
  const GoogleDriveTreeItem = ({ item, level = 0, parentPath = '' }) => {
    const itemId = item.id;
    const itemName = item.name || item.id;
    const isFolder = item.type === 'folder' || item.mimeType === 'application/vnd.google-apps.folder';
    const isExpanded = googleDriveExpandedNodes.includes(itemId);
    const contents = googleDriveFolderTree[itemId] || { folders: [], files: [] };
    const isLoading = googleDriveLoadingNodes[itemId];
    const isSelected = googleDriveSelectedFolder?.id === itemId;
    
    const currentPath = parentPath ? `${parentPath}/${itemName}` : itemName;
    
    const handleExpand = async () => {
      if (isExpanded) {
        setGoogleDriveExpandedNodes(prev => prev.filter(id => id !== itemId));
      } else {
        // Check connection status before expanding
        if (!googleDriveConnectorConnected) {
          // toast.warning('Google Drive connector is disconnected. Please reconnect to view folder contents.');
          return;
        }
        
        // Ensure we're using the correct assigned connector
        if (!googleDriveConnectionId) {
          // toast.warning('No Google Drive connector selected. Please ensure a connector is assigned to the flow.');
          return;
        }
        
        setGoogleDriveExpandedNodes(prev => [...prev, itemId]);
        if (!googleDriveFolderTree[itemId]) {
          // Ensure we have a valid token from the assigned connector before fetching
          let token = googleDriveAccessToken;
          if (!token) {
            token = await getGoogleDriveAccessToken();
            if (!token) {
              toast.error('Failed to get Google Drive access token. Please reconnect the connector.');
              setGoogleDriveExpandedNodes(prev => prev.filter(id => id !== itemId));
              return;
            }
            setGoogleDriveAccessToken(token);
          }
          
          // Verify token is for the current connector (if connector changed, get new token)
          // This ensures we don't mix data from different connectors
          console.log('Expanding folder:', itemId, 'using connector:', googleDriveConnectionId);
          await fetchGoogleDriveFolderContents(itemId, token);
        }
      }
    };
    
    const handleSelect = (event, checked) => {
      event.stopPropagation();
      if (checked) {
        setGoogleDriveSelectedFolder({ id: itemId, name: itemName, path: currentPath });
        setSelectedTargetGoogleDriveFolder(itemId);
      } else {
        setGoogleDriveSelectedFolder(null);
        setSelectedTargetGoogleDriveFolder('');
      }
    };
    
    if (!isFolder) {
      // File item (no expansion, no checkbox)
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, pl: level * 2 + 4, '&:hover': { backgroundColor: 'action.hover' } }}>
          <Box sx={{ width: 24, mr: 0.5 }} /> {/* Spacer for alignment */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, ml: 0.5 }}>
            {getFileIcon(itemName, item.mimeType)}
            <Typography variant="body2" sx={{ ml: 1 }}>{itemName}</Typography>
            {item.size && (
              <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                ({(item.size / 1024).toFixed(1)} KB)
              </Typography>
            )}
          </Box>
        </Box>
      );
    }
    
    // Folder item
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, pl: level * 2, '&:hover': { backgroundColor: 'action.hover' } }}>
          <IconButton size="small" onClick={handleExpand} disabled={isLoading} sx={{ width: 24, height: 24, mr: 0.5 }}>
            {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
          <Checkbox
            checked={isSelected}
            onChange={(e) => handleSelect(e, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            size="small"
            sx={{ p: 0.5 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, ml: 0.5 }}>
            {isExpanded ? <FolderOpen fontSize="small" color="primary" sx={{ mr: 1 }} /> : <Folder fontSize="small" sx={{ mr: 1 }} />}
            <Typography variant="body2">{itemName}</Typography>
          </Box>
          {isLoading && (<CircularProgress size={16} sx={{ ml: 1 }} />)}
        </Box>
        {isExpanded && (
          <Box>
            {/* Render subfolders */}
            {contents.folders.map((folder) => (
              <GoogleDriveTreeItem key={folder.id} item={folder} level={level + 1} parentPath={currentPath} />
            ))}
            {/* Render files */}
            {contents.files.map((file) => (
              <GoogleDriveTreeItem key={file.id} item={file} level={level + 1} parentPath={currentPath} />
            ))}
            {contents.folders.length === 0 && contents.files.length === 0 && !isLoading && (
              <Box sx={{ pl: level * 2 + 4, py: 0.5 }}>
                <Typography variant="caption" color="textSecondary">Empty folder</Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // ==================== Mapping Viewer Functions (Read-Only) ====================
  
  // Fetch source folders for mapping viewer
  const fetchViewSourceFolders = async (mcId, projectId) => {
    if (!mcId || !projectId) {
      setViewSourceFolders([]);
      return;
    }
    const flowIdForApi = config?.flowIdForConnector ?? config?.masterFlowId;
    const tenantIdValue = tenantId;
    if (!flowIdForApi || !tenantIdValue) {
      console.warn('Mapping viewer: Missing flowId or tenantId');
      setViewSourceFolders([]);
      return;
    }
    try {
      const url = `${AUTODESK_DOCS_API_URL}/folders/by-project?mcId=${mcId}&flowId=${flowIdForApi}&tenantId=${tenantIdValue}&projectId=${projectId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const folders = data.results || data.data || [];
      setViewSourceFolders(folders);
    } catch (error) {
      console.error('Mapping viewer: Error fetching source folders:', error);
      setViewSourceFolders([]);
    }
  };
  
  // Fetch target folders for mapping viewer
  const fetchViewTargetFolders = async (folderId = 'root') => {
    if (!googleDriveConnectionId || !googleDriveConnectorConnected) {
      setViewTargetFolders([]);
      return;
    }
    
    let accessToken = googleDriveAccessToken;
    if (!accessToken) {
      accessToken = await getGoogleDriveAccessToken();
      if (!accessToken) {
        console.error('Mapping viewer: No access token available for Google Drive');
        setViewTargetFolders([]);
        return;
      }
    }
    
    const parentId = folderId === 'root' ? 'root' : folderId;
    const query = `'${parentId}' in parents and trashed = false`;
    
    try {
      const foldersQuery = `mimeType = 'application/vnd.google-apps.folder' and ${query}`;
      const filesQuery = `mimeType != 'application/vnd.google-apps.folder' and ${query}`;
      const foldersUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(foldersQuery)}&fields=files(id,name,parents,mimeType)&pageSize=1000&supportsAllDrives=true&includeItemsFromAllDrives=true`;
      const filesUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(filesQuery)}&fields=files(id,name,parents,mimeType,size)&pageSize=1000&supportsAllDrives=true&includeItemsFromAllDrives=true`;
      
      const [foldersResponse, filesResponse] = await Promise.all([
        fetch(foldersUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        }),
        fetch(filesUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        })
      ]);
      
      if (!foldersResponse.ok || !filesResponse.ok) {
        throw new Error(`HTTP error! folders: ${foldersResponse.status}, files: ${filesResponse.status}`);
      }
      
      const foldersData = await foldersResponse.json();
      const filesData = await filesResponse.json();
      
      const folders = (foldersData.files || []).map(f => ({
        id: f.id,
        name: f.name,
        parents: f.parents || [],
        mimeType: f.mimeType,
        type: 'folders'
      }));
      
      const files = (filesData.files || []).map(f => ({
        id: f.id,
        name: f.name,
        parents: f.parents || [],
        mimeType: f.mimeType,
        size: f.size ? parseInt(f.size, 10) : null,
        type: 'items'
      }));
      
      const combined = [...folders, ...files];
      setViewTargetFolders(combined);
      return combined;
    } catch (error) {
      console.error('Mapping viewer: Error fetching target folders:', error);
      setViewTargetFolders([]);
      return [];
    }
  };
  
  // Fetch subfolder contents for mapping viewer
  const fetchViewSubfolderContents = async (folderId, projectId, isSource) => {
    if (isSource) {
      // Fetch Autodesk subfolder contents
      if (!autodeskDocsConnector?.mcId || !projectId || !folderId) return;
      const flowIdForApi = config?.flowIdForConnector ?? config?.masterFlowId;
      const tenantIdValue = tenantId;
      if (!flowIdForApi || !tenantIdValue) return;
      
      try {
        const url = `${AUTODESK_DOCS_API_URL}/folders/children?mcId=${autodeskDocsConnector.mcId}&flowId=${flowIdForApi}&tenantId=${tenantIdValue}&projectId=${projectId}&folderId=${folderId}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (!response.ok) return;
        const data = await response.json();
        const contents = data.results || data.data || [];
        setViewSubfolderContents(prev => ({ ...prev, [folderId]: { data: contents, projectId } }));
      } catch (error) {
        console.error('Mapping viewer: Error fetching Autodesk subfolder contents:', error);
      }
    } else {
      // Fetch Google Drive subfolder contents
      if (!googleDriveConnectionId || !googleDriveConnectorConnected) return;
      
      let accessToken = googleDriveAccessToken;
      if (!accessToken) {
        accessToken = await getGoogleDriveAccessToken();
        if (!accessToken) return;
      }
      
      const query = `'${folderId}' in parents and trashed = false`;
      
      try {
        const foldersQuery = `mimeType = 'application/vnd.google-apps.folder' and ${query}`;
        const filesQuery = `mimeType != 'application/vnd.google-apps.folder' and ${query}`;
        const foldersUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(foldersQuery)}&fields=files(id,name,parents,mimeType)&pageSize=1000&supportsAllDrives=true&includeItemsFromAllDrives=true`;
        const filesUrl = `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(filesQuery)}&fields=files(id,name,parents,mimeType,size)&pageSize=1000&supportsAllDrives=true&includeItemsFromAllDrives=true`;
        
        const [foldersResponse, filesResponse] = await Promise.all([
          fetch(foldersUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
          }),
          fetch(filesUrl, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
          })
        ]);
        
        if (!foldersResponse.ok || !filesResponse.ok) return;
        
        const foldersData = await foldersResponse.json();
        const filesData = await filesResponse.json();
        
        const folders = (foldersData.files || []).map(f => ({
          id: f.id,
          name: f.name,
          parents: f.parents || [],
          mimeType: f.mimeType,
          type: 'folders'
        }));
        
        const files = (filesData.files || []).map(f => ({
          id: f.id,
          name: f.name,
          parents: f.parents || [],
          mimeType: f.mimeType,
          size: f.size ? parseInt(f.size, 10) : null,
          type: 'items'
        }));
        
        setViewSubfolderContents(prev => ({ ...prev, [folderId]: { data: [...folders, ...files] } }));
      } catch (error) {
        console.error('Mapping viewer: Error fetching Google Drive subfolder contents:', error);
      }
    }
  };
  
  // Load mapping data for viewer
  const loadViewMappingData = async (mapping) => {
    if (!mapping) {
      setViewSourceFolders([]);
      setViewTargetFolders([]);
      setViewSourceExpandedFolders({});
      setViewTargetExpandedFolders({});
      setViewSubfolderContents({});
      return;
    }
    
    setViewLoading(true);
    try {
      // Reset state
      setViewSourceExpandedFolders({});
      setViewTargetExpandedFolders({});
      setViewSubfolderContents({});
      
      // Fetch source folders
      if (mapping.srcProjectId && autodeskDocsConnector?.mcId) {
        await fetchViewSourceFolders(autodeskDocsConnector.mcId, mapping.srcProjectId);
      } else {
        setViewSourceFolders([]);
      }
      
      // Fetch target folders
      const targetFolderId = mapping.dstGoogleDriveFolderId || mapping.dstFolderId || 'root';
      if (googleDriveConnectorConnected && googleDriveConnectionId) {
        await fetchViewTargetFolders(targetFolderId);
      } else {
        setViewTargetFolders([]);
      }
    } catch (error) {
      console.error('Mapping viewer: Error loading mapping data:', error);
    } finally {
      setViewLoading(false);
    }
  };
  
  // Effect to load mapping data when mapping index changes
  useEffect(() => {
    if (!googleDriveMappings || googleDriveMappings.length === 0) {
      setViewMappingIndex(0);
      setViewSourceFolders([]);
      setViewTargetFolders([]);
      return;
    }
    
    const safeIndex = Math.min(viewMappingIndex, googleDriveMappings.length - 1);
    if (safeIndex !== viewMappingIndex) {
      setViewMappingIndex(safeIndex);
      return;
    }
    
    const mapping = googleDriveMappings[safeIndex];
    if (mapping) {
      loadViewMappingData(mapping);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleDriveMappings, viewMappingIndex, config?.masterFlowId, tenantId, autodeskDocsConnector, googleDriveConnectorConnected, googleDriveConnectionId]);
  
  // Computed values for mapping viewer
  const currentViewMapping = useMemo(() => {
    if (!googleDriveMappings || googleDriveMappings.length === 0) return null;
    const safeIndex = Math.min(viewMappingIndex, googleDriveMappings.length - 1);
    return googleDriveMappings[safeIndex] || null;
  }, [googleDriveMappings, viewMappingIndex]);
  
  const viewSelectedSourceIds = useMemo(() => {
    if (!currentViewMapping) return [];
    if (Array.isArray(currentViewMapping.srcFolderInfo) && currentViewMapping.srcFolderInfo.length > 0) {
      return currentViewMapping.srcFolderInfo.map(f => f.id).filter(Boolean);
    }
    return currentViewMapping.srcFolderIds || [];
  }, [currentViewMapping]);
  
  // Folder Row component for mapping viewer
  const ViewFolderRow = ({ file, projectId, isSource, level = 0 }) => {
    const folderId = `${file.id}_${level}`;
    const isExpanded = isSource ? viewSourceExpandedFolders[folderId] : viewTargetExpandedFolders[folderId];
    const subfolders = isExpanded ? (viewSubfolderContents[file.id]?.data || []) : [];
    const isFolder = file.type === 'folders' || file.mimeType === 'application/vnd.google-apps.folder';
    const isSelectedForCopying = isSource && viewSelectedSourceIds.includes(file.id);
    
    const handleToggle = () => {
      if (!isFolder) return;
      
      if (isExpanded) {
        if (isSource) {
          setViewSourceExpandedFolders(prev => {
            const newState = { ...prev };
            delete newState[folderId];
            return newState;
          });
        } else {
          setViewTargetExpandedFolders(prev => {
            const newState = { ...prev };
            delete newState[folderId];
            return newState;
          });
        }
      } else {
        if (isSource) {
          setViewSourceExpandedFolders(prev => ({ ...prev, [folderId]: true }));
        } else {
          setViewTargetExpandedFolders(prev => ({ ...prev, [folderId]: true }));
        }
        
        // For source: pass projectId; for target: projectId is null, just use file.id
        if (file.id) {
          fetchViewSubfolderContents(file.id, projectId, isSource);
        }
      }
    };
    
    return (
      <>
        <TableRow
          hover
          onClick={handleToggle}
          sx={{
            cursor: isFolder ? 'pointer' : 'default',
            pl: level * 2,
            backgroundColor: isSelectedForCopying ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
            borderLeft: isSelectedForCopying ? '3px solid #1976d2' : 'none',
            '&:hover': {
              backgroundColor: isSelectedForCopying ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          <TableCell sx={{ pl: `${4 + level * 2}px` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isFolder ? (isExpanded ? '📂' : '📁') : '📄'}
              <Typography
                variant="body2"
                color={level > 0 ? 'text.secondary' : 'inherit'}
                sx={{
                  fontWeight: isSelectedForCopying ? 'bold' : 'normal',
                  color: isSelectedForCopying ? '#1976d2' : 'inherit'
                }}
              >
                {file.attributes?.displayName || file.attributes?.name || file.name || 'Unknown'}
              </Typography>
              {isSelectedForCopying && (
                <Chip
                  label="Selected"
                  size="small"
                  color="primary"
                  sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          </TableCell>
          <TableCell>
            <Chip
              label={isFolder ? 'Folder' : 'File'}
              size="small"
              color={isFolder ? 'primary' : 'default'}
            />
          </TableCell>
          <TableCell>
            {file.size
              ? `${(file.size / 1024).toFixed(2)} KB`
              : file.attributes?.storageSize
              ? `${(file.attributes.storageSize / 1024).toFixed(2)} KB`
              : isFolder ? '-' : '-'}
          </TableCell>
        </TableRow>
        
        {/* Recursively render subfolders */}
        {isExpanded && isFolder && level < 8 && subfolders.length > 0 && subfolders.map((subfolder, idx) => (
          <ViewFolderRow
            key={`subfolder-${folderId}-${idx}`}
            file={subfolder}
            projectId={projectId}
            isSource={isSource}
            level={level + 1}
          />
        ))}
      </>
    );
  };

  const persistGoogleDriveMappings = async (mappings, autodeskDocsConnector) => {
    if (!config?.masterFlowId) {
      throw new Error('Master flow ID is missing');
    }

    const jsonString = JSON.stringify(mappings);
    const MAX_CHUNK = 240;
    const existingMap = new Map(
      (googleDriveMappingParams || []).map(param => [param.sfpName, param])
    );
    const paramsToSave = [];
    const namesToKeep = new Set();

    const resolveMcId = (existingParam) => {
      if (existingParam?.sfpMcId) return existingParam.sfpMcId;
      if (autodeskDocsConnector?.mcId) return autodeskDocsConnector.mcId;
      if (googleDriveConnector?.mcId) return googleDriveConnector.mcId;
      return undefined;
    };

    const addParam = (name, value) => {
      namesToKeep.add(name);
      const existing = existingMap.get(name);
      const param = {
        sfpName: name,
        sfpValue: value,
        sfpMfId: config.masterFlowId,
        sfpId: existing?.sfpId || generateUUID(),
      };
      const mcId = resolveMcId(existing);
      if (mcId) {
        param.sfpMcId = mcId;
      }
      paramsToSave.push(param);
    };

    if (jsonString.length <= MAX_CHUNK) {
      addParam('GoogleDriveMappings', jsonString);
    } else {
      const chunkSize = MAX_CHUNK;
      let index = 1;
      for (let i = 0; i < jsonString.length; i += chunkSize) {
        const chunk = jsonString.substring(i, i + chunkSize);
        addParam(`GoogleDriveMappings_${index}`, chunk);
        index++;
      }
      addParam('GoogleDriveMappings', '[]');
    }

    const paramsToDelete = (googleDriveMappingParams || []).filter(param => !namesToKeep.has(param.sfpName));

    if (paramsToDelete.length > 0) {
      const deletePromises = paramsToDelete.map(param =>
        fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/sync-flow-properties/${param.sfpId}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        })
      );
      const deleteResults = await Promise.allSettled(deletePromises);
      const failedDeletion = deleteResults.find(result =>
        result.status === 'rejected' ||
        (result.status === 'fulfilled' && !result.value.ok)
      );
      if (failedDeletion) {
        throw new Error('Failed to clean up previous Google Drive mapping parameters');
      }
    }

    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/sync-flow-properties`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      body: JSON.stringify(paramsToSave),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(errorText || `Failed to save configuration (status ${response.status})`);
    }

    const savedParams = await response.json();
    setGoogleDriveMappingParams(savedParams);
    return savedParams;
  };

  const handleSaveGoogleDriveConfiguration = async () => {
    if (!config?.masterFlowId) return;

    // Use autodeskSelectedFolders from tree view, fallback to selectedSourceFolders for backward compatibility
    const foldersToSave = autodeskSelectedFolders.length > 0 ? autodeskSelectedFolders : selectedSourceFolders;
    if (foldersToSave.length === 0) {
      toast.error('Please select at least one folder to sync');
      return;
    }
    if (!autodeskDocsConnector) {
      toast.error('Autodesk Docs connector not found');
      return;
    }
    try {
      // Build a single mapping from current selection
      const srcProjectName = (projects.find(p => p.id === selectedSourceProject)?.attributes?.name?.name) || (projects.find(p => p.id === selectedSourceProject)?.attributes?.name) || selectedSourceProject;

      const trimmedMappingName = mappingName ? mappingName.trim() : '';
      const effectiveMappingName = trimmedMappingName.length > 0 ? trimmedMappingName : null;
      
      // Get Google Drive folder name if folder is selected
      let dstGoogleDriveFolderName = null;
      const targetFolderId = googleDriveSelectedFolder?.id || selectedTargetGoogleDriveFolder;
      if (targetFolderId && targetFolderId !== 'root') {
        const selectedFolder = googleDriveFolders.find(f => f.id === targetFolderId);
        dstGoogleDriveFolderName = selectedFolder ? selectedFolder.name : (googleDriveSelectedFolder?.name || null);
      } else if (targetFolderId === 'root') {
        dstGoogleDriveFolderName = 'Root (My Drive)';
      }
      
      // Store folder info with names and paths for display
      const srcFolderInfo = foldersToSave.map(f => ({
        id: f.id,
        name: f.name || f.id,
        path: f.path || f.name || f.id // Store full path
      }));
      
      const mapping = {
        srcProjectId: selectedSourceProject,
        srcProjectName,
        mappingName: effectiveMappingName,

        srcFolderIds: foldersToSave.map(f => f.id),
        srcFolderInfo: srcFolderInfo, // Store folder info with names and paths for display
        // Include target Google Drive folder ID and name if selected
        ...(targetFolderId && targetFolderId !== 'root' ? { 
          dstGoogleDriveFolderId: targetFolderId,
          ...(dstGoogleDriveFolderName ? { dstGoogleDriveFolderName } : {})
        } : {}),
      };

      const newMappings = editingIndex !== null
        ? googleDriveMappings.map((m, idx) => (idx === editingIndex ? mapping : m))
        : [...googleDriveMappings, mapping];
      setGoogleDriveMappings(newMappings);
      await persistGoogleDriveMappings(newMappings, autodeskDocsConnector);
      const displayName = effectiveMappingName || srcProjectName;
      toast.success(`${editingIndex !== null ? 'Mapping updated' : 'Mapping saved'}: ${displayName}`);
      setMappingsCollapsed(true);
      // reset current selection for next mapping
      setSelectedSourceProject('');
      setSelectedSourceFolders([]);

      setAutodeskSelectedFolders([]);
      setSourceFolders([]);

      setSelectedTargetGoogleDriveFolder('');
      setGoogleDriveSelectedFolder(null);
      setEditingIndex(null);
      setMappingName('');
    } catch (e) {
      toast.error(`Failed to save configuration: ${e.message}`);
    }
  };

  const handleDeleteMapping = async (index) => {
    if (!autodeskDocsConnector) {
      toast.error('Autodesk Docs connector not found');
      return;
    }
    try {
      const newMappings = googleDriveMappings.filter((_, i) => i !== index);
      setGoogleDriveMappings(newMappings);
      await persistGoogleDriveMappings(newMappings, autodeskDocsConnector);
      toast.success('Mapping deleted');
      if (editingIndex === index) {
        setMappingName('');
        setEditingIndex(null);
      }
    } catch (e) {
      toast.error(`Failed to delete mapping: ${e.message}`);
    }
  };

  const handleEditMapping = async (index) => {
    const m = googleDriveMappings[index];
    if (!m) return;
    setEditingIndex(index);
    setMappingsCollapsed(false);
    setSelectedSourceProject(m.srcProjectId);
    setMappingName(m.mappingName || '');

    // Pre-fill selected folders using srcFolderInfo if available (includes paths), otherwise use ids
    if (m.srcFolderInfo && Array.isArray(m.srcFolderInfo) && m.srcFolderInfo.length > 0) {
      const folderInfo = m.srcFolderInfo.map(f => ({
        id: f.id, 
        name: f.name || f.id,
        path: f.path || f.name || f.id,
      }));
      setSelectedSourceFolders(folderInfo);
      setAutodeskSelectedFolders(folderInfo.map(f => ({ ...f, projectId: m.srcProjectId })));
    } else {
      const fallbackFolders = (m.srcFolderIds || []).map(id => ({ id, name: id, path: id }));
      setSelectedSourceFolders(fallbackFolders);
      setAutodeskSelectedFolders(fallbackFolders.map(f => ({ ...f, projectId: m.srcProjectId })));
    }
    // Pre-fill target Google Drive folder if it exists
    const folderId = m.dstGoogleDriveFolderId || m.dstFolderId || '';
    setSelectedTargetGoogleDriveFolder(folderId);
    if (folderId) {
      const folderName = m.dstGoogleDriveFolderName || m.dstFolderName || (folderId === 'root' ? 'Root (My Drive)' : folderId);
      setGoogleDriveSelectedFolder({
        id: folderId === 'root' ? 'root' : folderId,
        name: folderName,
        path: folderName,
      });
    } else {
      setGoogleDriveSelectedFolder(null);
    }
    // Load folders for the project
    if (autodeskDocsConnector && m.srcProjectId) {
      await fetchFolders(autodeskDocsConnector.mcId, m.srcProjectId);
    }

    // Refresh Google Drive folders if connector is available
    if (googleDriveConnector && googleDriveConnector.mcId) {
      await fetchGoogleDriveFolders(googleDriveConnector.mcId, null, googleDriveConnector);
    }
  };

  const handleRunGoogleDriveNow = async () => {
    if (!config?.masterFlowId) {
      toast.error('Missing flow id');
      return;
    }
    try {
      setIsRunning(true);
      // Ensure mappings are persisted before run
      if (!autodeskDocsConnector) {
        toast.error('Autodesk Docs connector not found');
        setIsRunning(false);
        return;
      }
      if (!googleDriveMappings || googleDriveMappings.length === 0) {
        toast.error('No mappings configured. Please add and save a mapping first.');
        setIsRunning(false);
        return;
      }
      await persistGoogleDriveMappings(googleDriveMappings, autodeskDocsConnector);
      // brief delay to allow transaction commit on backend before fetching parameters for run
      await new Promise(res => setTimeout(res, 300));
      // Use instance ID so backend runs this flow instance (params, connectors, run status are per-instance)
      const flowIdForRun = flowInstanceId || config?.flowInstanceId || config?.masterFlowId;
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/transfer/autodesk-docs-to-google-drive?flowId=${flowIdForRun}&runType=Manual`, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        toast.success('Google Drive sync run started/completed');
        // Refresh sync records to show new sync results
        if (config?.masterFlowId) {
          const token = localStorage.getItem('token');
          const records = await fetchSyncRecords(config.masterFlowId, token);
          setSyncRecords(records);
          await loadSavedMappings(config.masterFlowId);
        }
      } else {
        toast.error(`Run failed: ${data.message || resp.status}`);
      }
    } catch (e) {
      toast.error(`Run error: ${e.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  // Get unique source projects from sync records and mappings
  const availableSourceProjects = useMemo(() => {
    const projectMap = new Map();
    
    // Add projects from mappings (with names)
    googleDriveMappings.forEach(mapping => {
      if (mapping.srcProjectId) {
        projectMap.set(mapping.srcProjectId, {
          id: mapping.srcProjectId,
          name: mapping.srcProjectName || mapping.srcProjectId
        });
      }
    });
    
    // Add projects from sync records (may not have names)
    syncRecords.forEach(record => {
      if (record.sourceProjectId && !projectMap.has(record.sourceProjectId)) {
        projectMap.set(record.sourceProjectId, {
          id: record.sourceProjectId,
          name: record.sourceProjectId // Fallback to ID if name not available
        });
      }
    });
    
    return Array.from(projectMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [syncRecords, googleDriveMappings]);


  // Calculate status counts for filter chips
  const statusCounts = useMemo(() => {
    const total = syncRecords.length;
    const synced = syncRecords.filter(r => (r.status || '').toLowerCase() === 'success').length;
    const notSynced = syncRecords.filter(r => {
      const status = (r.status || '').toLowerCase();
      return status === 'failed' || status === 'error' || status === 'skipped';
    }).length;
    return { total, synced, notSynced };
  }, [syncRecords]);

  // Filter and process sync records
  const filteredRecords = useMemo(() => {
    let filtered = [...syncRecords];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(record => {
        const sourceDesc = (record.sourceDesc || '').toLowerCase();
        const targetDesc = (record.targetDesc || '').toLowerCase();
        const status = (record.status || '').toLowerCase();
        return sourceDesc.includes(term) || targetDesc.includes(term) || status.includes(term);
      });
    }
    

    // Apply status filter (matching copy files logic)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => {
        const status = (record.status || '').toLowerCase();

        if (statusFilter === 'synced') return status === 'success';
        if (statusFilter === 'not-synced') {
          return status === 'failed' || status === 'error' || status === 'skipped';
        }
        return true;
      });
    }
    
    // Apply source project filter
    if (sourceProjectFilter !== 'all') {
      filtered = filtered.filter(record => {
        return record.sourceProjectId === sourceProjectFilter;
      });
    }
    
    return filtered;
  }, [syncRecords, searchTerm, statusFilter, sourceProjectFilter]);

  // Pagination
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredRecords.slice(startIndex, endIndex);
  }, [filteredRecords, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage);


  // Format file size helper
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  // Format status with proper capitalization
  const formatStatus = (status) => {
    if (!status) return 'UNKNOWN';
    const statusLower = status.toLowerCase();
    if (statusLower === 'success') return 'Synced';
    if (statusLower === 'failed' || statusLower === 'error') return 'FAILED';
    if (statusLower === 'skipped') return 'SKIPPED';
    return status.toUpperCase();
  };

  // Extract user-friendly message (hide internal version tracking)
  const formatMessage = (message) => {
    if (!message) return '-';
    // If message contains " | version:", split and take only the user-friendly part
    if (message.includes(' | version:')) {
      return message.split(' | version:')[0];
    }
    return message;
  };

  const showSourceColumns = Boolean(autodeskDocsConnector && autodeskConnected);
  const showTargetColumns = Boolean(googleDriveConnector && googleDriveConnectorConnected);
  const connectorsMissing = !showSourceColumns && !showTargetColumns;

  useEffect(() => {
    if (!showSourceColumns) {
      setAccountId('');
      setProjects([]);
      setSelectedSourceProject('');
      setAutodeskSelectedFolders([]);
      setSourceFolders([]);
      setAutodeskFolderTree({});
      setAutodeskExpandedNodes([]);
    }
  }, [showSourceColumns]);

  // Get Google Drive folder name from folder ID (if available)
  const getGoogleDriveFolderName = (folderId, storedName = null) => {
    if (!folderId || folderId.trim() === '') return 'Root';
    
    // Priority 1: Use stored folder name if available (most reliable)
    if (storedName && storedName.trim() !== '') {
      return storedName;
    }
    
    // Priority 2: Check folderNameCache (includes fetched subfolders)
    if (folderNameCache[folderId]) {
      return folderNameCache[folderId];
    }
    
    // Priority 3: Try to find in cached googleDriveFolders (root folders)
    const folder = googleDriveFolders.find(f => f.id === folderId);
    if (folder) {
      // Add to cache for consistency
      setFolderNameCache(prev => ({ ...prev, [folderId]: folder.name }));
      return folder.name;
    }
    
    // Priority 4: Check mappings for stored folder name
    if (googleDriveMappings.length > 0) {
      const mapping = googleDriveMappings.find(m => 
        (m.dstGoogleDriveFolderId === folderId || m.dstFolderId === folderId)
      );
      if (mapping && (mapping.dstGoogleDriveFolderName || mapping.dstFolderName)) {
        const mappingName = mapping.dstGoogleDriveFolderName || mapping.dstFolderName;
        // Add to cache
        setFolderNameCache(prev => ({ ...prev, [folderId]: mappingName }));
        return mappingName;
      }
    }
    
    // Priority 5: Trigger async fetch if not already fetching and not failed (returns null for now)
    // Check failedFolderIds to prevent infinite retries on 404 errors
    if (!fetchingFolderNames[folderId] && !failedFolderIds.has(folderId) && googleDriveConnector?.mcId) {
      fetchGoogleDriveFolderName(folderId).catch(err => {
        console.error('Error fetching folder name:', err);
      });
    }
    
    // Priority 6: Fallback to truncated ID while fetching
    const shortId = folderId.length > 20 ? `${folderId.substring(0, 20)}...` : folderId;
    return shortId;
  };

  // Format target column: Google Drive Folder > File Name
  const formatTargetColumn = (record) => {
    // For Google Drive flows, use the dstFolderId directly from the sync record
    // This is the most accurate source as it comes from the database
    let folderName = 'Root';
    
    // Priority 1: Use dstFolderId from the sync record (most accurate)
    if (record.targetFolderId || record.dstFolderId) {
      const folderId = record.targetFolderId || record.dstFolderId;
      // Check if there's a stored folder name in the record (if backend adds it)
      const storedName = record.targetFolderName || record.dstFolderName;
      folderName = getGoogleDriveFolderName(folderId, storedName);
    } 
    // Priority 2: Fallback to mappings if record doesn't have folder ID
    else if (record.sourceProjectId && googleDriveMappings.length > 0) {
      const mapping = googleDriveMappings.find(m => m.srcProjectId === record.sourceProjectId);
      if (mapping && (mapping.dstGoogleDriveFolderId || mapping.dstFolderId)) {
        const folderId = mapping.dstGoogleDriveFolderId || mapping.dstFolderId;
        const storedName = mapping.dstGoogleDriveFolderName || mapping.dstFolderName;
        folderName = getGoogleDriveFolderName(folderId, storedName);
      }
    }
    
    // Extract file name from targetDesc or targetId
    const fileName = record.targetDesc || record.targetId || record.targetFileName || 'Unknown';
    
    // Clean up folder name display - remove "Folder: " prefix if it's a real name (not truncated)
    // Truncated IDs are typically longer than 20 chars and show "..."
    let displayFolderName = folderName;
    if (folderName !== 'Root' && !folderName.includes('...') && folderName.length < 50) {
      // It's a real folder name, use as-is
      displayFolderName = folderName;
    } else if (folderName === 'Root') {
      displayFolderName = 'Root';
    } else {
      // It's a truncated ID, keep as-is
      displayFolderName = folderName;
    }
    
    return `${displayFolderName} > ${fileName}`;
  };


  // Render Mapping Viewer section
  const renderMappingViewer = () => {
    const hasMappings = Array.isArray(googleDriveMappings) && googleDriveMappings.length > 0;

    return (
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: '#f8f9fa',
            '&:hover': { backgroundColor: '#e9ecef' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Folder sx={{ color: '#1976d2' }} />
            <Typography variant="h6">
              Mapping Viewer (Read-Only)
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {!hasMappings ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No saved mappings found. Configure and save a mapping to view the folder structure snapshot here.
            </Alert>
          ) : (
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Mapping</InputLabel>
                <Select
                  value={Math.min(viewMappingIndex, googleDriveMappings.length - 1)}
                  label="Select Mapping"
                  onChange={(e) => setViewMappingIndex(Number(e.target.value))}
                >
                  {googleDriveMappings.map((mapping, idx) => {
                    const projectLabel = mapping.srcProjectName || mapping.srcProjectId || `Project ${idx + 1}`;
                    const targetId = mapping.dstGoogleDriveFolderId || mapping.dstFolderId || 'root';
                    const targetLabel = targetId === 'root'
                      ? 'Root (My Drive)'
                      : (mapping.dstGoogleDriveFolderName || mapping.dstFolderName || targetId);
                    const mappingLabel = mapping.mappingName && mapping.mappingName.trim().length > 0
                      ? mapping.mappingName
                      : `Mapping ${idx + 1}`;
                    return (
                      <MenuItem key={`view-map-${idx}`} value={idx}>
                        {`${mappingLabel}: ${projectLabel} → ${targetLabel}`}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          )}

          {hasMappings && currentViewMapping && (
            <Box sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Mapping Details</Typography>
              <Typography variant="body2">
                <strong>Source:</strong> {currentViewMapping.srcProjectName || currentViewMapping.srcProjectId || 'Unknown'}
              </Typography>
              <Typography variant="body2">
                <strong>Target:</strong> {currentViewMapping.dstGoogleDriveFolderName || currentViewMapping.dstFolderName || 'Root (My Drive)'}
              </Typography>
              <Typography variant="body2">
                <strong>Selected Folders:</strong> {viewSelectedSourceIds.length} folder(s)
              </Typography>
            </Box>
          )}

          <Grid container spacing={2}>
            {/* Source Project Column */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Source Project Structure
                  </Typography>
                  
                  {!hasMappings ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <Typography variant="body2" color="text.secondary">No mappings selected</Typography>
                    </Box>
                  ) : viewLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 2 }}>Loading...</Typography>
                    </Box>
                  ) : viewSourceFolders.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <Typography variant="body2" color="text.secondary">No folders available</Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Size</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewSourceFolders.map((file) => (
                            <ViewFolderRow
                              key={file.id}
                              file={file}
                              projectId={currentViewMapping?.srcProjectId}
                              isSource={true}
                              level={0}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Target Folder Column */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Target Folder Structure
                  </Typography>
                  
                  {!hasMappings ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <Typography variant="body2" color="text.secondary">No mappings selected</Typography>
                    </Box>
                  ) : !googleDriveConnectorConnected ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <Warning sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Google Drive connector is disconnected
                      </Typography>
                    </Box>
                  ) : viewLoading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ ml: 2 }}>Loading...</Typography>
                    </Box>
                  ) : viewTargetFolders.length === 0 ? (
                    <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                      <Typography variant="body2" color="text.secondary">No folders available</Typography>
                    </Box>
                  ) : (
                    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Size</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {viewTargetFolders.map((file) => (
                            <ViewFolderRow
                              key={file.id}
                              file={file}
                              projectId={null}
                              isSource={false}
                              level={0}
                            />
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/sync-flow')} startIcon={<ArrowBack />}>
          Back to Sync Flow
        </Button>
      </Box>
    );
  }

  // autodeskDocsConnector is now in state, set during fetchDataPointConfig

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>

      {/* Main Content */}
      <Box p={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/sync-flow')}>
            Back to Sync Flow
          </Button>
        </Box>
        {/* Enhanced Summary Card */}
        <Card sx={{ 
          mb: 3, 
          background: 'linear-gradient(135deg, #6B0F2A 0%, #8B1538 100%)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
              {config?.flowName || 'Google Drive Datapoint'}
            </Typography>

                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  Side-by-side comparison of source and target systems
            </Typography>
          </Box>

              <IconButton 
            onClick={async () => {
              if (config?.masterFlowId) {
                const token = localStorage.getItem('token');
                const records = await fetchSyncRecords(config.masterFlowId, token);
                setSyncRecords(records);
                toast.success('Sync records refreshed');
              }
            }}

                sx={{ 
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
            }}
          >

                <Refresh />
              </IconButton>
      </Box>

            <Grid container spacing={2}>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {syncRecords.length > 0 && (
                    <Chip 
                      icon={<SyncAlt />}
                      label={`Files Copied: ${syncRecords.length}`} 
                      sx={{ 
                        backgroundColor: 'rgba(255,255,255,0.2)', 
                        color: 'white',
                        fontWeight: 500,
                        '& .MuiChip-icon': { color: 'white' }
                      }} 
                    />
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Chip
                    label={`All (${statusCounts.total})`}
                    sx={{
                      backgroundColor: statusFilter === 'all' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' }
                    }}
                    onClick={() => setStatusFilter('all')}
                  />
                  <Chip
                    icon={<CheckCircle sx={{ color: 'white !important' }} />}
                    label={`${statusCounts.synced} Synced`}
                    sx={{
                      backgroundColor: statusFilter === 'synced' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                      '& .MuiChip-icon': { color: 'white !important' }
                    }}
                    onClick={() => setStatusFilter('synced')}
                  />
                  <Chip
                    icon={<Warning sx={{ color: 'white !important' }} />}
                    label={`${statusCounts.notSynced} Not Synced`}
                    sx={{
                      backgroundColor: statusFilter === 'not-synced' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                      '& .MuiChip-icon': { color: 'white !important' }
                    }}
                    onClick={() => setStatusFilter('not-synced')}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Google Drive Configuration */}
        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Settings sx={{ color: '#1976d2' }} />
                <Typography>Google Drive Configuration</Typography>
              </Box>
              <Button variant="contained" size="small" onClick={handleRunGoogleDriveNow} disabled={isRunning}>
                {isRunning ? 'Running…' : 'Run Now'}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Existing saved mappings collapsed list */}
            {googleDriveMappings.length > 0 && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">Saved Mappings</Typography>
                    <Button size="small" onClick={() => setMappingsCollapsed(!mappingsCollapsed)}>
                      {mappingsCollapsed ? 'Expand' : 'Collapse'}
                    </Button>
                  </Box>
                  {!mappingsCollapsed && (
                    <List dense>
                      {googleDriveMappings.map((m, idx) => (
                        <ListItem key={`map-${idx}`} secondaryAction={
                          <Box>
                            <IconButton size="small" onClick={() => handleEditMapping(idx)} title="Edit">
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteMapping(idx)} title="Delete">
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        }>
                          <ListItemText 
                            primary={
                              m.mappingName && m.mappingName.trim().length > 0
                                ? m.mappingName
                                : (m.srcProjectName || m.srcProjectId)
                            } 

                          secondary={(() => {
                            const folderCount = m.srcFolderIds?.length || 0;
                            if (folderCount === 0) return 'No folders selected';
                            
                            // Try to get folder paths from srcFolderInfo first (new format)
                            if (m.srcFolderInfo && Array.isArray(m.srcFolderInfo) && m.srcFolderInfo.length > 0) {
                              // Prioritize path over name, fallback to name, then id
                              const folderPaths = m.srcFolderInfo.map(f => f.path || f.name || f.id).filter(Boolean);
                              if (folderPaths.length > 0) {
                                // Show all folder paths
                                const folderPathsDisplay = folderPaths.join(', ');
                                const folderId = m.dstGoogleDriveFolderId || m.dstFolderId;
                                const folderName = getGoogleDriveFolderName(folderId, m.dstGoogleDriveFolderName || m.dstFolderName);
                                const targetDisplay = folderId ? ` → Target: ${folderName}` : ' → Root';
                                return `${folderPathsDisplay}${targetDisplay}`;
                              }
                            }
                            
                            // Fallback: try to get folder names from sourceFolders or folderTree
                            const folderNames = [];
                            if (m.srcFolderIds && m.srcFolderIds.length > 0) {
                              // First check if we have it in selectedSourceFolders (has path)
                              m.srcFolderIds.forEach(folderId => {
                                const selectedFolder = selectedSourceFolders.find(f => f.id === folderId);
                                if (selectedFolder && selectedFolder.path) {
                                  folderNames.push(selectedFolder.path);
                                } else {
                                  const folder = sourceFolders.find(f => f.id === folderId);
                                  if (folder) {
                                    // For root folders, just use the name
                                    const folderName = folder.attributes?.displayName || folder.attributes?.name || folder.name || folderId;
                                    folderNames.push(folderName);
                                  } else {
                                    // Try to build path from folderTree (would need buildFolderPath helper)
                                    folderNames.push(folderId);
                                  }
                                }
                              });
                            }
                            
                            if (folderNames.length > 0) {
                              // Show all folder names
                              const folderNamesDisplay = folderNames.join(', ');
                              const folderId = m.dstGoogleDriveFolderId || m.dstFolderId;
                              const folderName = getGoogleDriveFolderName(folderId, m.dstGoogleDriveFolderName || m.dstFolderName);
                              const targetDisplay = folderId ? ` → Target: ${folderName}` : ' → Root';
                              return `${folderNamesDisplay}${targetDisplay}`;
                            }
                            
                            // Final fallback: just show count
                            const folderId = m.dstGoogleDriveFolderId || m.dstFolderId;
                            const folderName = getGoogleDriveFolderName(folderId, m.dstGoogleDriveFolderName || m.dstFolderName);
                            const targetDisplay = folderId ? ` → Target: ${folderName}` : ' → Root';
                            return `Folders: ${folderCount}${targetDisplay}`;
                          })()}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            )}

            {showSourceColumns ? (
              <>
                {/* Add new mapping controls */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">Add Mapping</Typography>
                  <Button size="small" onClick={() => setMappingsCollapsed(false)}>Show Saved</Button>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Account ID *</InputLabel>
                    <Select
                      value={accountId}
                      onChange={async (e) => {
                        const hubId = e.target.value;
                        setAccountId(hubId);
                        setProjects([]);
                        setSelectedSourceProject('');
                        if (autodeskDocsConnector && hubId) {
                          await fetchProjects(autodeskDocsConnector.mcId, hubId, config?.masterFlowId, tenantId);
                          // Save AccountId parameter
                          try {
                            const accountParam = { sfpName: 'AccountId', sfpValue: hubId, sfpMfId: config.masterFlowId, sfpId: generateUUID() };
                            if (autodeskDocsConnector?.mcId) accountParam.sfpMcId = autodeskDocsConnector.mcId;
                            await fetch(`${process.env.REACT_APP_BACKEND_URL}synctrack/sync-flow-properties`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                              body: JSON.stringify([accountParam]),
                            });
                          } catch (e) {
                            console.warn('Failed to save AccountId:', e);
                          }
                        }
                      }}
                      label="Account ID *"
                      disabled={loadingHubs || !availableHubs.length}
                    >
                      {availableHubs.length === 0 ? (
                        <MenuItem value="">{loadingHubs ? 'Loading accounts...' : 'No accounts available'}</MenuItem>
                      ) : (
                        availableHubs.map((hub) => {
                          const hubId = hub.id || hub.hubId || '';
                          const hubName = hub.attributes?.name || hub.name || hubId;
                          return <MenuItem key={hubId} value={hubId}>{hubName} ({hubId})</MenuItem>;
                        })
                      )}
                    </Select>
                    {!accountId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        Account ID is required to load projects
                      </Typography>
                    )}
                  </FormControl>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Mapping Name (optional)"
                    value={mappingName}
                    onChange={(e) => setMappingName(e.target.value)}
                    placeholder="e.g. Project Files to Google Drive"
                    sx={{ mb: 2 }}
                  />
                  <FormControl fullWidth size="small" required disabled={!accountId}>
                    <InputLabel>Source Project *</InputLabel>
                    <Select
                      value={selectedSourceProject}
                      onChange={async (e) => {
                        const pid = e.target.value;
                        setSelectedSourceProject(pid);
                        setAutodeskSelectedFolders([]);
                        setAutodeskFolderTree({});
                        setAutodeskExpandedNodes([]);
                        if (autodeskDocsConnector && pid) {
                          await fetchFolders(autodeskDocsConnector.mcId, pid, config?.masterFlowId, tenantId);
                          await initializeAutodeskTree(pid);
                        }
                      }}
                      label="Source Project *"
                      disabled={loadingProjects || !accountId}
                    >
                      {projects.length === 0 ? (
                        <MenuItem value="">{loadingProjects ? 'Loading projects...' : 'No projects available'}</MenuItem>
                      ) : (
                        projects.map((project) => {
                          const name = project.attributes?.name?.name || project.attributes?.name || project.id;
                          return <MenuItem key={project.id} value={project.id}>{name}</MenuItem>;
                        })
                      )}
                    </Select>
                  </FormControl>
                </Box>
              </>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Autodesk Docs connector is not assigned or connected. Source configuration is hidden until the connector is available.
              </Alert>
            )}


            {/* Filter dropdowns removed per request */}

            {/* Enhanced Folder Tree Views - Side by Side */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Autodesk Source Project Tree */}
              {showSourceColumns && (
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                        Autodesk Source Project
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                        Select folders to sync (checkboxes). Click folders to expand and view files.
                      </Typography>
                      <Paper variant="outlined" sx={{ maxHeight: 500, overflow: 'auto', p: 1, minHeight: 300 }}>
                        {!selectedSourceProject ? (
                          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                            <Typography variant="body2" color="textSecondary">Please select a source project first</Typography>
                          </Box>
                        ) : loadingSourceFolders ? (
                          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" sx={{ ml: 2 }}>Loading folders...</Typography>
                          </Box>
                        ) : sourceFolders.length === 0 ? (
                          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                            <Typography variant="body2" color="textSecondary">No folders available</Typography>
                          </Box>
                        ) : (
                          sourceFolders.map((folder) => {
                            const treeItem = {
                              id: folder.id,
                              attributes: folder.attributes,
                              type: 'folders'
                            };
                            return (
                              <AutodeskTreeItem 
                                key={folder.id} 
                                item={treeItem} 
                                level={0} 
                                projectId={selectedSourceProject} 
                                parentPath="" 
                              />
                            );
                          })
                        )}
                      </Paper>

                      {autodeskSelectedFolders.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" color="primary">
                            {autodeskSelectedFolders.length} folder(s) selected
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Google Drive Target Folder Tree */}
              <Grid item xs={12} md={showSourceColumns ? 6 : 12}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Google Drive Target Folder
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 2, display: 'block' }}>
                      {googleDriveSelectedFolder
                        ? `Selected folder: ${googleDriveSelectedFolder.path}`
                        : `Select a folder in the tree below to use as the Google Drive destination.`}
                    </Typography>
                    <Paper variant="outlined" sx={{ maxHeight: 500, overflow: 'auto', p: 1, minHeight: 300 }}>
                      {!googleDriveConnector ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                          <Typography variant="body2" color="textSecondary">
                            Google Drive connector not found. Files will be saved to root folder.
                          </Typography>
                        </Box>
                      ) : !googleDriveConnectorConnected ? (
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={4}>
                          <Warning sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                          <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 1 }}>
                            Google Drive connector is disconnected.
                          </Typography>
                          <Typography variant="caption" color="textSecondary" align="center">
                            Please reconnect the connector to view folder structure.
                          </Typography>
                        </Box>
                      ) : (
                        <Box>
                          {/* Root level - show root folder or selected folder's contents */}
                          {(() => {
                            const startFolderId = 'root';
                            const rootContents = googleDriveFolderTree[startFolderId] || { folders: [], files: [] };

                            // If we haven't loaded root yet, show loading or initialize
                            if (!googleDriveFolderTree[startFolderId] && !googleDriveLoadingNodes[startFolderId]) {
                              // Initialize tree on mount
                              if (googleDriveAccessToken) {
                                fetchGoogleDriveFolderContents(startFolderId, googleDriveAccessToken);
                              } else {
                                initializeGoogleDriveTree(startFolderId);
                              }
                              return (
                                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                  <CircularProgress size={24} />
                                  <Typography variant="body2" sx={{ ml: 2 }}>Loading...</Typography>
                                </Box>
                              );
                            }

                            if (googleDriveLoadingNodes[startFolderId]) {
                              return (
                                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                                  <CircularProgress size={24} />
                                  <Typography variant="body2" sx={{ ml: 2 }}>Loading...</Typography>
                                </Box>
                              );
                            }

                            return (
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5, mb: 1, pl: 0 }}>
                                  <Checkbox 
                                    checked={googleDriveSelectedFolder?.id === 'root'} 
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setGoogleDriveSelectedFolder({ id: 'root', name: 'Root (My Drive)', path: 'Root' });
                                        setSelectedTargetGoogleDriveFolder('');
                                      } else {
                                        setGoogleDriveSelectedFolder(null);
                                        setSelectedTargetGoogleDriveFolder('');
                                      }
                                    }} 
                                    size="small" 
                                    sx={{ p: 0.5 }} 
                                  />
                                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, ml: 0.5 }}>
                                    <Folder fontSize="small" sx={{ mr: 1 }} />
                                    <Typography variant="body2" fontWeight="bold">Root (My Drive)</Typography>
                                  </Box>
                                </Box>
                                {/* Render root contents */}
                                {rootContents.folders.map((folder) => (
                                  <GoogleDriveTreeItem key={folder.id} item={folder} level={1} parentPath="Root" />
                                ))}
                                {rootContents.files.map((file) => (
                                  <GoogleDriveTreeItem key={file.id} item={file} level={1} parentPath="Root" />
                                ))}
                                {rootContents.folders.length === 0 && rootContents.files.length === 0 && (
                                  <Box sx={{ pl: 4, py: 0.5 }}>
                                    <Typography variant="caption" color="textSecondary">Empty folder</Typography>
                                  </Box>
                                )}
                              </Box>
                            );
                          })()}
                        </Box>
                      )}
                    </Paper>
                    {googleDriveSelectedFolder && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" color="primary">
                          Selected: {googleDriveSelectedFolder.name}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveGoogleDriveConfiguration}

                disabled={
                  !showSourceColumns ||
                  !accountId ||
                  !selectedSourceProject ||
                  (autodeskSelectedFolders.length === 0 && selectedSourceFolders.length === 0)
                }
                size="large"
              >
                {editingIndex !== null ? 'Update Mapping' : 'Save Mapping'}
              </Button>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Sync Records Table */}

        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              backgroundColor: '#f8f9fa',
              '&:hover': { backgroundColor: '#e9ecef' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <SyncAlt sx={{ color: '#1976d2' }} />
              <Typography variant="h6">
                Sync Records ({filteredRecords.length} items)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {/* Enhanced Filters */}
            <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                <TextField

                    fullWidth
                  size="small"

                    placeholder="Search files..."
                  value={searchTerm}

                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  InputProps={{

                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                  <InputLabel>Source Project</InputLabel>
                  <Select
                    value={sourceProjectFilter}
                    onChange={(e) => {
                      setSourceProjectFilter(e.target.value);

                        setCurrentPage(1);
                    }}
                    label="Source Project"
                  >
                    <MenuItem value="all">All Projects</MenuItem>
                    {availableSourceProjects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);

                        setCurrentPage(1);
                    }}
                    label="Status"
                  >
                    <MenuItem value="all">All</MenuItem>

                      <MenuItem value="synced">Synced</MenuItem>
                      <MenuItem value="not-synced">Not Synced</MenuItem>
                  </Select>
                </FormControl>

                </Grid>
              </Grid>
            </Box>

            {/* Simplified Table Controls */}
            <Box sx={{ p: 1.5, backgroundColor: '#f8f9fa', borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {paginatedRecords.length} of {filteredRecords.length} items
                  {searchTerm && ` • Filtered by "${searchTerm}"`}
                </Typography>
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="small"
                    onClick={async () => {
                      if (config?.masterFlowId) {
                        const token = localStorage.getItem('token');
                        const records = await fetchSyncRecords(config.masterFlowId, token);
                        setSyncRecords(records);
                        toast.success('Sync records refreshed');
                      }
                    }}
                    title="Refresh data"
                  >
                    <Refresh fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            </Box>


            {/* Connector availability notice */}
            {connectorsMissing && (
              <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', backgroundColor: '#fff8e1' }}>
                <Alert severity="warning" sx={{ mb: 0 }}>
                  No connected Autodesk or Google Drive connectors assigned to this flow. Assign and connect at least one connector to view data.
                </Alert>
              </Box>
            )}

            {/* Table */}
            {paginatedRecords.length === 0 ? (

              <Box sx={{ p: 3, textAlign: 'center' }}>
              <Alert severity="info">No sync records found</Alert>

              </Box>
            ) : (
              <>

                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>

                        {showSourceColumns && (
                          <>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Source Path</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center' }}>Source Size</TableCell>
                          </>
                        )}
                        {showTargetColumns && (
                          <>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Target</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center' }}>Target Size</TableCell>
                          </>
                        )}
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center', width: 100 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'center', width: 180 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Message</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', textAlign: 'right', width: 180 }}>Verify</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>

                      {paginatedRecords.map((record, index) => {
                        const statusFormatted = formatStatus(record.status);
                        const statusLower = (record.status || '').toLowerCase();
                        
                        return (
                          <TableRow
                            key={index}
                            sx={{
                              '&:hover': { backgroundColor: '#f5f5f5' }
                            }}
                          >
                            {showSourceColumns && (
                              <>
                                <TableCell>

                                  <Typography variant="body2">
                                    {record.sourceDesc || record.sourceData || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {formatFileSize(record.sourceFileSize)}
                                  </Typography>
                                </TableCell>
                              </>
                            )}
                            {showTargetColumns && (
                              <>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatTargetColumn(record)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {formatFileSize(record.targetFileSize || record.dstFileSize || (!showSourceColumns ? record.sourceFileSize : null))}
                                  </Typography>
                                </TableCell>
                              </>
                            )}
                            <TableCell align="center">
                              {(() => {
                                // Determine label and color based on status (matching copy files colors)
                                let backgroundColor = '#fff3e0';
                                let color = '#e65100';

                                if (statusLower === 'success') {
                                  backgroundColor = '#e8f5e9';
                                  color = '#2e7d32';
                                } else if (statusLower === 'failed' || statusLower === 'error') {
                                  backgroundColor = '#ffebee';
                                  color = '#c62828';
                                } else if (statusLower === 'skipped') {
                                  backgroundColor = '#fff3e0';
                                  color = '#f57c00';
                                }

                                return (
                                  <Chip
                                    label={statusFormatted}
                              size="small"

                                    sx={{
                                      backgroundColor,
                                      color,
                                      fontWeight: 500,
                                      textTransform: statusLower === 'success' ? 'none' : 'uppercase'
                                    }}
                                  />
                                );
                              })()}
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color="text.secondary">
                                {record.syncDateTime 
                                  ? new Date(record.syncDateTime).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true
                                    })
                                  : 'N/A'}
                              </Typography>
                          </TableCell>
                          <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {formatMessage(record.message)}
                              </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {(() => {
                              const verifyState = verifyStatus[record.logId];
                              const isVerifying = Boolean(verifyState?.loading);
                              const verifyResult = verifyState?.data;
                              const chipStyles = verifyResult ? getVerificationChipStyles(verifyResult.status) : null;
                              return (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                  {verifyResult?.status && (
                                    <Chip
                                      size="small"
                                      label={formatVerificationStatus(verifyResult.status)}
                                      sx={{
                                        fontWeight: 500,
                                        ...chipStyles
                                      }}
                                    />
                                  )}
                                  <Tooltip title={renderVerificationTooltipContent(verifyResult)}>
                                    <span>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => verifySyncRecord(record)}
                                        disabled={isVerifying || !record.logId}
                                      >
                                        {isVerifying ? (
                                          <CircularProgress size={16} />
                                        ) : verifyResult?.status ? 'Re-Verify' : 'Verify'}
                                      </Button>
                                    </span>
                                  </Tooltip>
                                </Box>
                              );
                            })()}
                          </TableCell>
                        </TableRow>

                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>


                {/* Pagination */}
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Page {currentPage} of {totalPages}
                  </Typography>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(e, value) => setCurrentPage(value)}
                      color="primary"

                    size="small"
                    />
                  </Box>
              </>
            )}

          </AccordionDetails>
        </Accordion>

        {renderMappingViewer()}
      </Box>
    </Box>
  );
};

export default GoogleDriveDatapoint;



